import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { CollaborationAction, CollaborationConnection, CollaborationReply, CollaborationWork, CollaborationWorkInput, CollaborationBackgroundInput, CollaborationBackgroundReply } from './collaborationTypes';
import { AUTH_REQUIRED_EVENT } from '../auth/events';
import type {
  BackupDestinationCheck,
  BackupJobView,
  BackupRunRequest,
  BackupSettings,
  BackupsOverview,
} from './backupTypes';
import { getServerUrl } from '../utils/tauri';
import { BUILD_HEADER, noteServerBuild } from '../updates/pageBuild';
import type { DirectorAdoptionPlan, DirectorAdoptionReport, DirectorCollection, DirectorCutoutRequest, DirectorCutoutResult, DirectorDiscovery, DirectorFramingDraft, DirectorFramingDraftView, DirectorFramingPreview, DirectorFramingRequest, DirectorIdentity, DirectorIdentityPage, DirectorMappingPage, DirectorFeasibility, DirectorMosaicPreview, DirectorResolvedName,
  DirectorSkyMarks,
  DirectorSkyMarksQuery, DirectorActivation, DirectorActivationPushReport, DirectorActivationReport, DirectorPlanDraft, DirectorPlanList, DirectorPlanView, DirectorRigStatusView, DirectorRigProfileSummary, DirectorTemplateList, DirectorStatus, DirectorRigPlan, DirectorRigProfileEdit, DirectorRigProfileView, DirectorRigReport, DirectorSurvey, DirectorNameSearch, DirectorLibraryTemplate, DirectorAttached, DirectorTakenValues, DirectorRigScheduling, DirectorPlanProgress, DirectorPlanDeletion, DirectorPlanDeletionOutcome, DirectorCollaborationLeave, DirectorCollaborationLeft } from './directorTypes';
import type { GuidFillReport, GuidReport, RenamedLightsMerged, RenamedLightsReport,
  ProjectMosaic,
  ProjectProcessingSettings,
  StackColorInputSources,
  FlatHistoryState,
  SchedulerFlatHistoryPage,
  CalibrationSettings,
  CalibrationRuleValue,
  CalibrationRuleValues,
  StackAutomationSettings,
  StackMasterSource,
  StackCalibrationMasters,
  ExternalMasterPolicy,
  DarkChoice,
  RemoteImageUploadDirectoryTemplateSource,
  ApiResponse,
  ExportLayout,
  ExportSettings,
  WbppOptions,
  PixInsightSettings,
  WbppRunStatus,
  StartWbppRunRequest,
  AstroBinDetail,
  AstroBinExport,
  AstroBinFilterEntry,
  AstroBinFilterMap,
  AstroBinSettings,
  Project,
  Target,
  TargetNavigation,
  OrganizationOperation,
  OrganizationPreview,
  OrganizationResult,
  OrganizationDestinations,
  Image,
  ImageQuery,
  BatchGradeEntry,
  BatchGradeResponse,
  UpdateGradeRequest,
  StarDetectionResponse,
  PreviewOptions,
  ServerInfo,
  AccountPreferences,
  AuthStatus,
  AuthTokenSummary,
  AuthUserSummary,
  CreateAuthTokenRequest,
  CreateAuthUserRequest,
  MintedAuthToken,
  UpdateAuthUserRequest,
  UpdateNoticeStatus,
  SchedulerSyncRequest,
  SchedulerSyncPreviewListEntry,
  SchedulerSyncPreviewResponse,
  SchedulerSyncResponse,
  DatabaseSummary,
  CreateDatabaseRequest,
  CreateDatabaseResponse,
  ImportRequest,
  ImportStatus,
  AutoImportSettings,
  ScanRules,
  AutoImportStatus,
  FileCheckResponse,
  DirectoryTreeResponse,
  ProjectOverview,
  TargetOverview,
  ProjectSchedulerDetails,
  DeletedProject,
  ProjectRemovalOutcome,
  ProjectRemovalPreview,
  ProjectSchedulerUpdate,
  TargetSchedulerUpdate,
  CreateExposurePlanRequest,
  ExposurePlanDetails,
  OverallStats,
  SkyCoverage,
  CacheRefreshProgress,
  SequenceAnalysisRequest,
  ProjectSequenceAnalysisRequest,
  DatabaseSequenceAnalysisRequest,
  SequenceAnalysisResponse,
  ImageQualityResponse,
  PenaltyScaleParams,
  SpatialScanRequest,
  SpatialScanStatus,
  SpatialScanStatusRequest,
  QualityBackfillRequest,
  QualityBackfillStatus,
  CalibratedCopies,
  PreviewDescriptor,
  ImagePreviewDescriptor,
  CalibrationMasterPreviewDescriptor,
  CalibrationFramePreviewDescriptor,
  CalibrationQualityStatus,
  FlatGroupsView,
  FlatLightChoiceOptions,
  FlatGroupUseView,
  FlatSessionPlace,
  FlatMeasureStatus,
  FrameSitesStatus,
  PausableJob,
  FlatRatioPreviewDescriptor,
  GenerationStatus,
  AstrometryAnalysis,
  SatelliteAnalysis,
  SatelliteAnalysisStatus,
  StackPreviewJob,
  CalibrationMode,
  StackFrameOrder,
  StackMethod,
  WbppActivity,
  JobsResponse,
  WbppStackImport,
  StackMethodSettings,
  WorkerSettings,
  StorageSettings,
  StorageFoldersUpdate,
  StorageLimitsUpdate,
  ArtifactSearchJob,
  ReferenceRegion,
  LatestStackPreviews,
  StackActivity,
  StackColorCatalog,
  StackColorCrop,
  StackColorJob,
  StackColorKind,
  StackColorProcessing,
  StackNarrowbandPalette,
  StackStretchPreview,
  StackViewProcessingRequest,
  AstrometryCapabilities,
  RcAstroCapabilities,
  StackStretchPendingProgress,
  AstrometryValidationReport,
  CatalogInstallPreset,
  CatalogInstallStatus,
  CalibrationLibrarySummary,
  CalibrationMutationOutcome,
  CalibrationDuplicateStatus,
  CalibrationFrameDetail,
  CalibrationFramePage,
  CalibrationProjectScope,
  CalibrationFrameSize,
  CalibrationFrameStretch,
  CalibrationFrameView,
  CalibrationListFilters,
  CalibrationSessionPage,
  LibraryCalibrationMasters,
  PeerSummary,
  PeerCheck,
  RemoteSyncRequest,
  RemoteSyncResult,
  ProcessingSetup,
  ProcessingSetupKind,
  ProcessingSetupsDocument,
  ProcessingSetupsImportResult,
} from './types';

// Store the initialized API instance and server URL
let initializedApi: AxiosInstance | null = null;
let cachedServerUrl: string | null = null;

export class CollaborationRequestError extends Error {
  readonly status: number | undefined;
  constructor(message: string, status: number | undefined) {
    super(message);
    this.name = 'CollaborationRequestError';
    this.status = status;
  }
}

// Initialize the API client
const initializeApi = async () => {
  if (!initializedApi) {
    const serverUrl = await getServerUrl();
    cachedServerUrl = serverUrl;

    const baseURL = serverUrl ? `${serverUrl}/api` : '/api';
    initializedApi = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Every reply names the server's frontend build. A page served by that
    // same server can be reloaded into it; the desktop app's page ships
    // inside the app, so it has nothing to reload.
    const noteBuild = (response?: AxiosResponse) => {
      if (!serverUrl) noteServerBuild(response?.headers[BUILD_HEADER], response?.headers['cache-control']);
    };

    // Add response interceptor for error handling
    initializedApi.interceptors.response.use(
      (response) => {
        noteBuild(response);
        return response;
      },
      (error) => {
        if (axios.isAxiosError<ApiResponse<unknown>>(error)) {
          noteBuild(error.response);
          if (
            error.response?.status === 401
            && !error.config?.url?.startsWith('/auth/')
          ) {
            window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
          }
          const message = error.response?.data?.error;
          // Axios retains request bodies and one-time sign-in replies.
          if (error.config?.url?.match(/^\/director\/v1\/(?:collaboration\/|rigs\/[^/]+\/collaboration)/)) {
            console.error('Collaboration API error:', { status: error.response?.status });
            return Promise.reject(new CollaborationRequestError(message || 'Collaboration request failed', error.response?.status));
          }
          console.error('API Error:', error);
          if (message) return Promise.reject(new Error(message, { cause: error }));
        } else {
          console.error('API Error:', error);
        }
        return Promise.reject(error);
      }
    );
  }
  return initializedApi;
};

// Get the API instance (initialize if needed)
const getApi = async () => {
  return await initializeApi();
};

// Get cached server URL for synchronous URL building
const getCachedServerUrl = (): string => {
  return cachedServerUrl || '';
};

// Build a per-DB path under /api/db/{dbId}.
const dbPath = (dbId: string, path: string) => `/db/${encodeURIComponent(dbId)}${path}`;

const withServerUrl = (path: string): string => `${getCachedServerUrl()}${path}`;

export interface AstroBinExportParams {
  project_id?: number;
  target_id?: number;
  include_pending?: boolean;
  detail?: AstroBinDetail;
}

const astroBinQuery = (params: AstroBinExportParams): Record<string, string> => {
  const query: Record<string, string> = {};
  if (params.project_id !== undefined) query.project_id = String(params.project_id);
  if (params.target_id !== undefined) query.target_id = String(params.target_id);
  if (params.include_pending) query.include_pending = 'true';
  if (params.detail && params.detail !== 'essentials') query.detail = params.detail;
  return query;
};

// Bump when response cache semantics change. Version 2 moves rendered images
// from a 24-hour freshness lifetime to mandatory revalidation; changing the
// URL prevents an older browser entry from hiding that new policy.
const RENDERED_ARTIFACT_URL_VERSION = '2';

const normalizeStretchPreview = (preview: StackStretchPreview): StackStretchPreview => ({
  ...preview,
  deconvolution_version: preview.deconvolution_version ?? null,
  deconvolution_id: preview.deconvolution_id ?? null,
  deconvolution: preview.deconvolution ?? null,
  preview_url: withServerUrl(preview.preview_url),
  original_preview_url: withServerUrl(preview.original_preview_url),
  fits_url: preview.fits_url ? withServerUrl(preview.fits_url) : null,
  stars_preview_url: preview.stars_preview_url ? withServerUrl(preview.stars_preview_url) : null,
  stars_original_preview_url: preview.stars_original_preview_url
    ? withServerUrl(preview.stars_original_preview_url) : null,
  stars_fits_url: preview.stars_fits_url ? withServerUrl(preview.stars_fits_url) : null,
});

const normalizeColorJob = (job: StackColorJob): StackColorJob => ({
  ...job,
  resolved_input_rc_astro: Object.fromEntries(
    Object.entries(job.resolved_input_rc_astro ?? {}).map(([role, result]) => [role, {
      ...result,
      fits_url: withServerUrl(result.fits_url),
      stars_fits_url: result.stars_fits_url ? withServerUrl(result.stars_fits_url) : null,
    }])
  ),
  resolved_color_rc_astro: job.resolved_color_rc_astro
    ? { ...job.resolved_color_rc_astro,
        fits_url: withServerUrl(job.resolved_color_rc_astro.fits_url) }
    : null,
});

/** No answer from the server, or a gateway's while it is down. Not a
 *  request the page cancelled. */
const serverUnreachable = (cause: unknown): boolean =>
  axios.isAxiosError(cause) &&
  !axios.isCancel(cause) &&
  (!cause.response || [502, 503, 504].includes(cause.response.status));

const stackStretchError = (cause: unknown, fallback: string): Error => {
  if (axios.isAxiosError<ApiResponse<unknown>>(cause)) {
    return new Error(cause.response?.data?.error || cause.message || fallback);
  }
  return cause instanceof Error ? cause : new Error(fallback);
};

/** What a stack build is asked for, alone or split by channel. */
export type StackPreviewStartRequest = {
  image_ids: number[];
  accepted_only: boolean;
  force?: boolean;
  /** Reproject onto the shared north-up, east-left grid. Off by default. */
  north_up?: boolean;
  /**
   * Integration order. `capture` (default) is chronological; `quality`
   * puts the best-graded frames first, which makes the progressive
   * signal-to-noise curve a reading of which frames are worth keeping.
   */
  order?: StackFrameOrder;
  /**
   * Scoring overrides (penalty scales, absolute reject limits) so stack
   * frame exclusion agrees with every other scoring surface.
   */
  scoring?: PenaltyScaleParams;
  /** How to calibrate the lights: auto (default), on (forced), or off. */
  calibration?: CalibrationMode;
  /** How the frames integrate; omitted uses the server's stacking method. */
  method?: StackMethod;
  /** Per-channel exceptions to `calibration`, by target and filter. */
  calibration_overrides?: Array<{
    target_id: number;
    filter_name: string;
    exposure_group_key?: string;
    calibration: CalibrationMode;
  }>;
  /**
   * The display pipeline a target's first color preview gets once this
   * build leaves none of its channels waiting. Omitted, the server only
   * rebuilds the color previews already composed for the target.
   */
  color_defaults?: StackColorProcessing;
  /**
   * Build only this channel of the images. The others still lend their
   * reference frames for the pier-side mapping, so this is the same build
   * Build stacks queues for the channel.
   */
  channel?: { target_id: number; filter_name: string; exposure_group_key?: string };
};

export const apiClient = {
  getTransfers: async (): Promise<import('./transferTypes').TransferActivity> => {
    const api = await getApi();
    const result = await api.get<ApiResponse<import('./transferTypes').TransferActivity>>('/director/v1/transfers');
    if (!result.data.data) throw new Error(result.data.error || 'Could not load transfers');
    return result.data.data;
  },
  getCollaborationConnections: async (rig: string): Promise<CollaborationConnection[]> => {
    const api = await getApi();
    const result = await api.get<ApiResponse<CollaborationConnection[]>>(`/director/v1/rigs/${encodeURIComponent(rig)}/collaboration`);
    if (!result.data.data) throw new Error(result.data.error || 'Could not load collaboration connections');
    return result.data.data;
  },
  collaborationWork: async (id: string, input: CollaborationWorkInput): Promise<CollaborationWork> => {
    const api = await getApi();
    const result = await api.post<ApiResponse<CollaborationWork>>(`/director/v1/collaboration/${encodeURIComponent(id)}/work`, input);
    if (!result.data.data) throw new Error(result.data.error || 'Collaboration request failed');
    return result.data.data;
  },
  collaborationBackground: async (id: string, input: CollaborationBackgroundInput): Promise<CollaborationBackgroundReply> => {
    const api = await getApi();
    const result = await api.post<ApiResponse<CollaborationBackgroundReply>>(`/director/v1/collaboration/${encodeURIComponent(id)}/work`, input);
    if (!result.data.data) throw new Error(result.data.error || 'Background refresh request failed');
    return result.data.data;
  },
  createCollaborationConnection: async (rig: string, input: { id: string; server_url: string; name: string; allow_loopback_http: boolean }): Promise<CollaborationConnection> => {
    const api = await getApi();
    const result = await api.post<ApiResponse<CollaborationConnection>>(`/director/v1/rigs/${encodeURIComponent(rig)}/collaboration`, input);
    if (!result.data.data) throw new Error(result.data.error || 'Could not save collaboration connection');
    return result.data.data;
  },
  collaborationAction: async (id: string, action: CollaborationAction, code?: string): Promise<CollaborationReply | CollaborationConnection> => {
    const api = await getApi();
    const result = await api.post<ApiResponse<CollaborationReply | CollaborationConnection>>(`/director/v1/collaboration/${encodeURIComponent(id)}/${action}`, code === undefined ? {} : { code });
    if (!result.data.data) throw new Error(result.data.error || 'Collaboration request failed');
    return result.data.data;
  },
  getDirectorStatus: async (): Promise<DirectorStatus> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorStatus>>('/director/v1/status');
    if (!data.data) throw new Error(data.error || 'Failed to load Director status');
    return data.data;
  },

  discoverDirectorCatalog: async (slug: string): Promise<DirectorDiscovery> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorDiscovery>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/discovery`);
    if (!data.data) throw new Error(data.error || 'Failed to inspect catalog');
    return data.data;
  },
  getDirectorMappings: async (slug: string, after?: string): Promise<DirectorMappingPage> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorMappingPage>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/mappings`, { params: { after, limit: 256 } });
    if (!data.data) throw new Error(data.error || 'Failed to load catalog mappings');
    return data.data;
  },
  previewDirectorAdoption: async (slug: string, plan: DirectorAdoptionPlan): Promise<DirectorAdoptionReport> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorAdoptionReport>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/adoption/preview`, plan);
    if (!data.data) throw new Error(data.error || 'Failed to preview mappings');
    return data.data;
  },
  previewDirectorRig: async (slug: string, plan: DirectorRigPlan): Promise<DirectorRigReport> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorRigReport>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/rig/preview`, plan);
    if (!data.data) throw new Error(data.error || 'Failed to preview database rig');
    return data.data;
  },
  applyDirectorRig: async (slug: string, plan: DirectorRigPlan, preview_digest: string): Promise<DirectorRigReport> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorRigReport>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/rig/apply`, { plan, preview_digest });
    if (!data.data) throw new Error(data.error || 'Failed to enable database planning');
    return data.data;
  },
  getDirectorRigProfile: async (slug: string): Promise<DirectorRigProfileView> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorRigProfileView>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/rig/profile`);
    if (!data.data) throw new Error(data.error || 'Failed to load rig profile');
    return data.data;
  },

  saveDirectorRigProfile: async (slug: string, edit: DirectorRigProfileEdit): Promise<DirectorRigProfileView> => {
    const api = await getApi();
    const { data } = await api.put<ApiResponse<DirectorRigProfileView>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/rig/profile`, edit);
    if (!data.data) throw new Error(data.error || 'Failed to save rig profile');
    return data.data;
  },

  getDirectorSiteProfile: async (site: string): Promise<import('./directorTypes').DirectorSiteProfileView> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<import('./directorTypes').DirectorSiteProfileView>>(`/director/v1/sites/${encodeURIComponent(site)}/profile`);
    if (!data.data) throw new Error(data.error || 'Failed to load the site');
    return data.data;
  },

  saveDirectorSiteProfile: async (site: string, edit: import('./directorTypes').DirectorSiteProfileEdit): Promise<import('./directorTypes').DirectorSiteProfileView> => {
    const api = await getApi();
    const { data } = await api.put<ApiResponse<import('./directorTypes').DirectorSiteProfileView>>(`/director/v1/sites/${encodeURIComponent(site)}/profile`, edit);
    if (!data.data) throw new Error(data.error || 'Failed to save the site');
    return data.data;
  },

  /** Read N.I.N.A. `.hrz` text into a horizon; nothing is stored. */
  parseDirectorHorizon: async (text: string): Promise<import('./directorTypes').DirectorHorizon> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<import('./directorTypes').DirectorHorizon>>('/director/v1/horizons/parse', { text });
    if (!data.data) throw new Error(data.error || 'Failed to read the horizon file');
    return data.data;
  },

  getDirectorSurveys: async (): Promise<DirectorSurvey[]> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorSurvey[]>>('/director/v1/sky/surveys');
    if (!data.data) throw new Error(data.error || 'Failed to load surveys');
    return data.data;
  },

  /** One survey image. 202 and 502 are answers, not errors: the view polls or explains. */
  fetchDirectorCutout: async (request: DirectorCutoutRequest, signal?: AbortSignal): Promise<DirectorCutoutResult> => {
    const api = await getApi();
    // Bytes, not a Blob: the browser and the test runner both hand those back
    // without touching a stream, and the JSON answers decode from the same buffer.
    const response = await api.get<ArrayBuffer>('/director/v1/sky/cutout', {
      params: request, responseType: 'arraybuffer', signal, validateStatus: status => status === 200 || status === 202 || status === 502,
    });
    const type = String(response.headers['content-type'] ?? 'image/jpeg');
    if (response.status === 200) return { state: 'ready', blob: new Blob([response.data], { type }) };
    if (response.status === 202) return { state: 'generating' };
    let error = 'Survey image is unavailable';
    try { error = (JSON.parse(new TextDecoder().decode(response.data)) as ApiResponse<unknown>).error || error; } catch { /* keep the default */ }
    return { state: 'failed', error };
  },

  previewDirectorFraming: async (request: DirectorFramingRequest): Promise<DirectorFramingPreview> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorFramingPreview>>('/director/v1/framing/preview', request);
    if (!data.data) throw new Error(data.error || 'Failed to compute framing');
    return data.data;
  },

  getDirectorFramingDraft: async (projectId: string): Promise<DirectorFramingDraftView> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorFramingDraftView>>(`/director/v1/projects/${encodeURIComponent(projectId)}/framing`);
    if (!data.data) throw new Error(data.error || 'Failed to load framing');
    return data.data;
  },

  saveDirectorFramingDraft: async (draft: DirectorFramingDraft): Promise<DirectorFramingDraftView> => {
    const api = await getApi();
    const { data } = await api.put<ApiResponse<DirectorFramingDraftView>>(`/director/v1/projects/${encodeURIComponent(draft.project_id)}/framing`, draft);
    if (!data.data) throw new Error(data.error || 'Failed to save framing');
    return data.data;
  },

  getDirectorRigProfiles: async (): Promise<DirectorRigProfileSummary[]> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorRigProfileSummary[]>>('/director/v1/rigs/profiles');
    if (!data.data) throw new Error(data.error || 'Failed to load rig profiles');
    return data.data;
  },

  getDirectorTemplates: async (slug: string): Promise<DirectorTemplateList> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorTemplateList>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/templates`);
    if (!data.data) throw new Error(data.error || 'Failed to load exposure templates');
    return data.data;
  },

  getDirectorTemplateLibrary: async (): Promise<DirectorLibraryTemplate[]> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorLibraryTemplate[]>>('/director/v1/templates');
    if (!data.data) throw new Error(data.error || 'Failed to load the template library');
    return data.data;
  },

  saveDirectorLibraryTemplate: async (template: Omit<DirectorLibraryTemplate, 'bandpass'>): Promise<DirectorLibraryTemplate> => {
    const api = await getApi();
    // Read models also carry a derived bandpass; never send it to the strict draft API.
    const payload = {
      id: template.id, revision: template.revision, name: template.name,
      filter_name: template.filter_name, gain: template.gain, offset: template.offset,
      bin: template.bin, readout_mode: template.readout_mode,
      default_exposure_seconds: template.default_exposure_seconds,
      moon: template.moon, updated_at_ms: template.updated_at_ms,
    };
    const { data } = await api.put<ApiResponse<DirectorLibraryTemplate>>(`/director/v1/templates/${encodeURIComponent(template.id)}`, payload);
    if (!data.data) throw new Error(data.error || 'Failed to save the template');
    return data.data;
  },

  deleteDirectorLibraryTemplate: async (id: string, revision: number): Promise<void> => {
    const api = await getApi();
    const { data } = await api.delete<ApiResponse<{ deleted: boolean }>>(`/director/v1/templates/${encodeURIComponent(id)}`, { params: { revision } });
    if (!data.data) throw new Error(data.error || 'Failed to delete the template');
  },

  /** What deleting a plan would delete, and why it cannot be, if so. */
  getDirectorPlanDeletion: async (projectId: string): Promise<DirectorPlanDeletion> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorPlanDeletion>>(`/director/v1/projects/${encodeURIComponent(projectId)}/deletion`);
    if (!data.data) throw new Error(data.error || 'Failed to read what deleting the plan removes');
    return data.data;
  },

  /** Delete a plan and its project in each database, as previewed. */
  deleteDirectorPlan: async (projectId: string, digests: Record<string, string>): Promise<DirectorPlanDeletionOutcome> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorPlanDeletionOutcome>>(
      `/director/v1/projects/${encodeURIComponent(projectId)}/deletion`, { digests });
    if (!data.data) throw new Error(data.error || 'Failed to delete the plan');
    return data.data;
  },

  /** What leaving the plan's collaboration removes and keeps. */
  getDirectorCollaborationLeave: async (projectId: string): Promise<DirectorCollaborationLeave> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorCollaborationLeave>>(`/director/v1/projects/${encodeURIComponent(projectId)}/collaboration/leave`);
    if (!data.data) throw new Error(data.error || 'Failed to read what leaving the collaboration removes');
    return data.data;
  },

  /** Leave the plan's collaboration: the plan becomes a plan of its own. */
  leaveDirectorCollaboration: async (projectId: string): Promise<DirectorCollaborationLeft> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorCollaborationLeft>>(
      `/director/v1/projects/${encodeURIComponent(projectId)}/collaboration/leave`, {});
    if (!data.data) throw new Error(data.error || 'Failed to leave the collaboration');
    return data.data;
  },

  /** Move every database link of another plan onto this one and retire it. */
  attachDirectorProject: async (projectId: string, fromProjectId: string): Promise<DirectorAttached> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorAttached>>(`/director/v1/projects/${encodeURIComponent(projectId)}/attach`, { from_project_id: fromProjectId });
    if (!data.data) throw new Error(data.error || 'Failed to attach the project');
    return data.data;
  },

  /** Give one database's project a plan of its own again. `dropRigWork`
   *  confirms that the plan's work for that database's rig goes with it;
   *  without it a plan that has some answers 409 saying what would go. */
  detachDirectorProject: async (projectId: string, catalogSlug: string, sourceProjectGuid: string, name: string, dropRigWork = false): Promise<DirectorIdentity> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorIdentity>>(`/director/v1/projects/${encodeURIComponent(projectId)}/detach`, { catalog_slug: catalogSlug, source_project_guid: sourceProjectGuid, name, drop_rig_work: dropRigWork });
    if (!data.data) throw new Error(data.error || 'Failed to detach the project');
    return data.data;
  },

  getObservingDefaults: async (): Promise<import('./directorPreferences').ObservingDefaults> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<import('./directorPreferences').ObservingDefaults>>('/director/v1/preferences');
    if (!data.data) throw new Error(data.error || 'Failed to load observing defaults');
    return data.data;
  },
  getObservingSettings: async (scope: import('./directorPreferences').PreferenceScope, id: string): Promise<import('./directorPreferences').ObservingSettings> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<import('./directorPreferences').ObservingSettings>>(`/director/v1/preferences/${scope}/${encodeURIComponent(id)}`);
    if (!data.data) throw new Error(data.error || 'Failed to load observing preferences');
    return data.data;
  },
  saveObservingSettings: async (settings: import('./directorPreferences').ObservingSettings): Promise<import('./directorPreferences').ObservingSettings> => {
    const api = await getApi();
    const { data } = await api.put<ApiResponse<import('./directorPreferences').ObservingSettings>>(`/director/v1/preferences/${settings.scope}/${encodeURIComponent(settings.scope_id)}`, settings);
    if (!data.data) throw new Error(data.error || 'Failed to save observing preferences');
    return data.data;
  },
  getEffectiveObserving: async (rig: string, project?: string): Promise<import('./directorPreferences').EffectiveObserving> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<import('./directorPreferences').EffectiveObserving>>(`/director/v1/rigs/${encodeURIComponent(rig)}/preferences`, { params: { project_id: project } });
    if (!data.data) throw new Error(data.error || 'Failed to resolve observing preferences');
    return data.data;
  },
  getDirectorPlan: async (projectId: string): Promise<DirectorPlanView> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorPlanView>>(`/director/v1/projects/${encodeURIComponent(projectId)}/plan`);
    if (!data.data) throw new Error(data.error || 'Failed to load plan');
    return data.data;
  },

  saveDirectorPlan: async (plan: DirectorPlanDraft): Promise<DirectorPlanView> => {
    const api = await getApi();
    const { data } = await api.put<ApiResponse<DirectorPlanView>>(`/director/v1/projects/${encodeURIComponent(plan.project_id)}/plan`, plan);
    if (!data.data) throw new Error(data.error || 'Failed to save plan');
    return data.data;
  },

  /** Each rig's frames per objective, read from its Target Scheduler database. */
  getDirectorPlanProgress: async (projectId: string): Promise<DirectorPlanProgress> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorPlanProgress>>(`/director/v1/projects/${encodeURIComponent(projectId)}/plan/progress`);
    if (!data.data) throw new Error(data.error || 'Failed to load plan progress');
    return data.data;
  },

  getDirectorActivation: async (projectId: string): Promise<DirectorActivation | null> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<{ activation: DirectorActivation | null }>>(`/director/v1/projects/${encodeURIComponent(projectId)}/activation`);
    if (!data.data) throw new Error(data.error || 'Failed to load activation');
    return data.data.activation;
  },

  /** What an activation would change, worked out on a copy of each rig
   *  database's planning tables: never written, never locked. */
  checkDirectorActivation: async (projectId: string): Promise<DirectorActivationReport> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorActivationReport>>(`/director/v1/projects/${encodeURIComponent(projectId)}/activation/check`);
    if (!data.data) throw new Error(data.error || 'Failed to check activation');
    return data.data;
  },

  /** Take one rig's Target Scheduler values into the saved plan, under the
   *  draft revisions last seen. */
  takeTargetSchedulerValues: async (projectId: string, rigId: string, planRevision: number, framingRevision: number): Promise<DirectorTakenValues> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorTakenValues>>(`/director/v1/projects/${encodeURIComponent(projectId)}/plan/take-target-scheduler`, { rig_id: rigId, plan_revision: planRevision, framing_revision: framingRevision });
    if (!data.data) throw new Error(data.error || "Failed to take Target Scheduler's values");
    return data.data;
  },

  /** A rig's scheduling limits against every project in its database: read only. */
  getRigScheduling: async (rigId: string): Promise<DirectorRigScheduling> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorRigScheduling>>(`/director/v1/rigs/${encodeURIComponent(rigId)}/scheduling`);
    if (!data.data) throw new Error(data.error || 'Failed to compare the rig limits');
    return data.data;
  },

  /** Write the reviewed limits; refused with 409 when anything changed since. */
  applyRigScheduling: async (rigId: string, digest: string): Promise<DirectorRigScheduling> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorRigScheduling>>(`/director/v1/rigs/${encodeURIComponent(rigId)}/scheduling/apply`, { digest });
    if (!data.data) throw new Error(data.error || 'Failed to apply the rig limits');
    return data.data;
  },

  getCollaborationActivation: async (projectId: string): Promise<import('./collaborationTypes').CollaborationActivationContext> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<import('./collaborationTypes').CollaborationActivationContext>>(`/director/v1/projects/${encodeURIComponent(projectId)}/activation/collaboration`);
    if (!data.data) throw new Error(data.error || 'Failed to load collaboration assignments');
    return data.data;
  },

  previewDirectorActivation: async (projectId: string, collaboration?: import('./collaborationTypes').CollaborationVisit[]): Promise<DirectorActivationReport> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorActivationReport>>(`/director/v1/projects/${encodeURIComponent(projectId)}/activation/preview`, collaboration?.length ? { collaboration } : {});
    if (!data.data) throw new Error(data.error || 'Failed to preview activation');
    return data.data;
  },

  applyDirectorActivation: async (projectId: string, preview_digest: string, collaboration?: import('./collaborationTypes').CollaborationVisit[]): Promise<DirectorActivationReport> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorActivationReport>>(`/director/v1/projects/${encodeURIComponent(projectId)}/activation/apply`, { preview_digest, ...(collaboration?.length ? { collaboration } : {}) });
    if (!data.data) throw new Error(data.error || 'Failed to apply activation');
    return data.data;
  },

  getDirectorMosaic: async (projectId: string): Promise<DirectorMosaicPreview> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorMosaicPreview>>(`/director/v1/projects/${encodeURIComponent(projectId)}/mosaic`);
    if (!data.data) throw new Error(data.error || 'Failed to load the mosaic preview');
    return data.data;
  },

  pushDirectorActivation: async (projectId: string): Promise<DirectorActivationPushReport> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorActivationPushReport>>(`/director/v1/projects/${encodeURIComponent(projectId)}/activation/push`, {});
    if (!data.data) throw new Error(data.error || 'Failed to push the activation');
    return data.data;
  },

  getDirectorPlans: async (): Promise<DirectorPlanList> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorPlanList>>('/director/v1/plans');
    if (!data.data) throw new Error(data.error || 'Failed to load plans');
    return data.data;
  },

  getDirectorRigStatuses: async (): Promise<DirectorRigStatusView[]> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorRigStatusView[]>>('/director/v1/rigs/status');
    if (!data.data) throw new Error(data.error || 'Failed to load rig status');
    return data.data;
  },

  getDirectorSkyMarks: async (query: DirectorSkyMarksQuery): Promise<DirectorSkyMarks> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorSkyMarks>>('/director/v1/sky/objects', { params: query });
    if (!data.data) throw new Error(data.error || 'Sky marks could not be loaded');
    return data.data;
  },

  searchDirectorNames: async (q: string, online = false): Promise<DirectorNameSearch> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorNameSearch>>('/director/v1/sky/search', { params: online ? { q, online: true } : { q } });
    if (!data.data) throw new Error(data.error || 'Name search failed');
    return data.data;
  },

  resolveDirectorName: async (name: string): Promise<DirectorResolvedName> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorResolvedName>>('/director/v1/sky/resolve', { params: { name } });
    if (!data.data) throw new Error(data.error || 'Name could not be resolved');
    return data.data;
  },

  getDirectorFeasibility: async (projectId: string, request: { nights?: number; center?: { ra_degrees: number; dec_degrees: number }; rig_centers?: Record<string, { ra_degrees: number; dec_degrees: number }>; start_ms?: number }): Promise<DirectorFeasibility> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorFeasibility>>(`/director/v1/projects/${encodeURIComponent(projectId)}/feasibility`, request);
    if (!data.data) throw new Error(data.error || 'Failed to compute feasibility');
    return data.data;
  },

  applyDirectorAdoption: async (slug: string, plan: DirectorAdoptionPlan, preview_digest: string): Promise<DirectorAdoptionReport> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorAdoptionReport>>(`/director/v1/catalogs/${encodeURIComponent(slug)}/adoption/apply`, { plan, preview_digest });
    if (!data.data) throw new Error(data.error || 'Failed to apply mappings');
    return data.data;
  },

  getDirectorIdentities: async (
    collection: DirectorCollection, after?: string,
  ): Promise<DirectorIdentityPage> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<DirectorIdentityPage>>(`/director/v1/${collection}`, {
      params: { limit: 64, after },
    });
    if (!data.data) throw new Error(data.error || 'Failed to load Director records');
    return data.data;
  },

  createDirectorIdentity: async (
    collection: DirectorCollection, request: { id: string; name: string },
  ): Promise<DirectorIdentity> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<DirectorIdentity>>(`/director/v1/${collection}`, request);
    if (!data.data) throw new Error(data.error || 'Failed to create Director record');
    return data.data;
  },

  renameDirectorIdentity: async (
    collection: DirectorCollection, id: string, request: { expected_revision: number; name: string },
  ): Promise<DirectorIdentity> => {
    const api = await getApi();
    const { data } = await api.patch<ApiResponse<DirectorIdentity>>(
      `/director/v1/${collection}/${encodeURIComponent(id)}`, request,
    );
    if (!data.data) throw new Error(data.error || 'Failed to rename Director record');
    return data.data;
  },

  getAuthStatus: async (): Promise<AuthStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AuthStatus>>('/auth/status');
    if (!data.data) throw new Error(data.error || 'Failed to check sign-in status');
    return data.data;
  },

  login: async (username: string, password: string): Promise<AuthStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<AuthStatus>>('/auth/login', {
      username,
      password,
    });
    if (!data.data) throw new Error(data.error || 'Sign in failed');
    return data.data;
  },

  logout: async (): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.post('/auth/logout');
  },

  /** The signed-in user's saved view preferences. */
  getMyPreferences: async (): Promise<AccountPreferences> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AccountPreferences>>('/me/preferences');
    if (!data.data) throw new Error(data.error || 'Failed to load your preferences');
    return data.data;
  },

  /** Merge changes into the signed-in user's view preferences: each key
   *  given replaces that preference, and `null` resets it to the default.
   *  Keys left out keep what the server has. With `keepalive` the request
   *  outlives the page, for a save made as it closes. */
  patchMyPreferences: async (
    changes: Record<string, unknown>,
    options: { keepalive?: boolean } = {},
  ): Promise<void> => {
    const body = { schema_version: 1, preferences: changes };
    if (options.keepalive) {
      await fetch(`${getCachedServerUrl()}/api/me/preferences`, {
        method: 'PATCH',
        keepalive: true,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return;
    }
    const apiInstance = await getApi();
    await apiInstance.patch<ApiResponse<AccountPreferences>>('/me/preferences', body);
  },

  getAuthUsers: async (): Promise<AuthUserSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AuthUserSummary[]>>('/auth/users');
    if (!data.data) throw new Error(data.error || 'Failed to load users');
    return data.data;
  },

  createAuthUser: async (request: CreateAuthUserRequest): Promise<AuthUserSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<AuthUserSummary[]>>(
      '/auth/users',
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to add user');
    return data.data;
  },

  updateAuthUser: async (
    username: string,
    request: UpdateAuthUserRequest
  ): Promise<AuthUserSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<AuthUserSummary[]>>(
      '/auth/users/' + encodeURIComponent(username),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to update user');
    return data.data;
  },

  removeAuthUser: async (username: string): Promise<AuthUserSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<AuthUserSummary[]>>(
      '/auth/users/' + encodeURIComponent(username)
    );
    if (!data.data) throw new Error(data.error || 'Failed to remove user');
    return data.data;
  },

  getAuthTokens: async (): Promise<AuthTokenSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AuthTokenSummary[]>>('/auth/tokens');
    if (!data.data) throw new Error(data.error || 'Failed to load API tokens');
    return data.data;
  },

  createAuthToken: async (request: CreateAuthTokenRequest): Promise<MintedAuthToken> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<MintedAuthToken>>(
      '/auth/tokens',
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to create the API token');
    return data.data;
  },

  revokeAuthToken: async (id: string): Promise<AuthTokenSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<AuthTokenSummary[]>>(
      '/auth/tokens/' + encodeURIComponent(id)
    );
    if (!data.data) throw new Error(data.error || 'Failed to revoke the API token');
    return data.data;
  },

  // ── Global ────────────────────────────────────────────────────────────────

  getCalibrationSettings: async (): Promise<CalibrationSettings> => {
    const apiInstance = await getApi();
    const { data } =
      await apiInstance.get<ApiResponse<CalibrationSettings>>('/settings/calibration');
    if (!data.data) throw new Error(data.error || 'Failed to get calibration settings');
    return data.data;
  },

  updateCalibrationSettings: async (update: {
    rotation_tolerance_deg?: number | null;
    external_masters?: ExternalMasterPolicy;
    flat_star_masking?: boolean;
    dark_reach_days?: number | null;
    complete_dark_frames?: number | null;
    dark_choice?: DarkChoice;
    flat_max_age_days?: number | null;
    exclude_uncalibrated?: boolean;
    /** Any tunable check by key; a key left out keeps its value, `null` restores the default. */
    rules?: Record<string, CalibrationRuleValue | null>;
  }): Promise<CalibrationSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<CalibrationSettings>>(
      '/settings/calibration',
      update
    );
    if (!data.data) throw new Error(data.error || 'Failed to update calibration settings');
    return data.data;
  },

  /** Replace every value a rig sets for itself; an empty `rules` inherits them all. */
  updateRigCalibrationSettings: async (
    rigUuid: string,
    update: { name?: string; rules: CalibrationRuleValues }
  ): Promise<CalibrationSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<CalibrationSettings>>(
      `/settings/calibration/rigs/${encodeURIComponent(rigUuid)}`,
      update
    );
    if (!data.data) throw new Error(data.error || "Failed to save the rig's calibration settings");
    return data.data;
  },

  /** The rig inherits every value again. */
  deleteRigCalibrationSettings: async (rigUuid: string): Promise<CalibrationSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<CalibrationSettings>>(
      `/settings/calibration/rigs/${encodeURIComponent(rigUuid)}`
    );
    if (!data.data) throw new Error(data.error || "Failed to reset the rig's calibration settings");
    return data.data;
  },

  getStackAutomationSettings: async (): Promise<StackAutomationSettings> => {
    const apiInstance = await getApi();
    const { data } =
      await apiInstance.get<ApiResponse<StackAutomationSettings>>('/settings/stacking');
    if (!data.data) throw new Error(data.error || 'Failed to get stack settings');
    return data.data;
  },

  getWorkerSettings: async (): Promise<WorkerSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<WorkerSettings>>('/settings/workers');
    if (!data.data) throw new Error(data.error || 'Failed to load the processor shares');
    return data.data;
  },

  /** `null` goes back to the server config file's share. */
  updateWorkerSettings: async (update: {
    interactive_ratio: number | null;
    background_ratio: number | null;
  }): Promise<WorkerSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<WorkerSettings>>('/settings/workers', update);
    if (!data.data) throw new Error(data.error || 'Failed to save the processor shares');
    return data.data;
  },

  getStorageSettings: async (): Promise<StorageSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StorageSettings>>('/settings/storage');
    if (!data.data) throw new Error(data.error || 'Failed to load the disk limit');
    return data.data;
  },

  /** Saves the limits and checks the volumes at once. */
  updateStorageSettings: async (limits: StorageLimitsUpdate): Promise<StorageSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<StorageSettings>>('/settings/storage', limits);
    if (!data.data) throw new Error(data.error || 'Failed to save the disk limit');
    return data.data;
  },

  /** Saves where the folders go from the next start on. */
  updateStorageFolders: async (folders: StorageFoldersUpdate): Promise<StorageSettings> => {
    const apiInstance = await getApi();
    // The response interceptor turns a refusal into its own message.
    const { data } = await apiInstance.put<ApiResponse<StorageSettings>>(
      '/settings/storage/folders',
      folders,
    );
    if (!data.data) throw new Error(data.error || 'Failed to save the folders');
    return data.data;
  },

  getStackMethod: async (): Promise<StackMethodSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StackMethodSettings>>(
      '/settings/stacking/method'
    );
    if (!data.data) throw new Error(data.error || 'Failed to load the stacking method');
    return data.data;
  },

  updateStackMethod: async (method: StackMethod): Promise<StackMethodSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<StackMethodSettings>>(
      '/settings/stacking/method',
      method
    );
    if (!data.data) throw new Error(data.error || 'Failed to save the stacking method');
    return data.data;
  },

  updateStackAutomationSettings: async (update: {
    automatic_previews: boolean;
    arrival_delay_minutes: number;
    grade_delay_minutes: number;
    build_new_channels: boolean;
    new_channel_window_days: number;
    reapply_processing?: boolean;
  }): Promise<StackAutomationSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<StackAutomationSettings>>(
      '/settings/stacking',
      update
    );
    if (!data.data) throw new Error(data.error || 'Failed to update stack settings');
    return data.data;
  },

  getExportSettings: async (): Promise<ExportSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ExportSettings>>('/settings/export');
    if (!data.data) throw new Error(data.error || 'Failed to get export settings');
    return data.data;
  },

  updateExportSettings: async (update: {
    default_layout: ExportLayout;
    /** Absent keeps the stored WBPP settings. */
    wbpp?: WbppOptions;
  }): Promise<ExportSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<ExportSettings>>('/settings/export', update);
    if (!data.data) throw new Error(data.error || 'Failed to update export settings');
    return data.data;
  },

  /** Settings › Backups: the schedule, the run under way, the archives. */
  getBackups: async (): Promise<BackupsOverview> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<BackupsOverview>>('/backups');
    if (!data.data) throw new Error(data.error || 'Failed to read backups');
    return data.data;
  },

  /** The run under way and the last one, for the job line. */
  getBackupStatus: async (): Promise<BackupJobView> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<BackupJobView>>('/backups/status');
    if (!data.data) throw new Error(data.error || 'Failed to read the backup');
    return data.data;
  },

  updateBackupSettings: async (settings: BackupSettings): Promise<BackupsOverview> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<BackupsOverview>>('/backups/settings', settings);
    if (!data.data) throw new Error(data.error || 'Failed to save the backup schedule');
    return data.data;
  },

  /** Queue a backup now; the server answers 202 while it runs. */
  runBackup: async (request: BackupRunRequest): Promise<BackupJobView> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<BackupJobView>>('/backups/run', request);
    if (!data.data) throw new Error(data.error || 'Failed to start the backup');
    return data.data;
  },

  stopBackup: async (): Promise<BackupJobView> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<BackupJobView>>('/backups/stop');
    if (!data.data) throw new Error(data.error || 'Failed to stop the backup');
    return data.data;
  },

  checkBackupDestination: async (path: string): Promise<BackupDestinationCheck> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<BackupDestinationCheck>>(
      '/backups/check-destination',
      { path }
    );
    if (!data.data) throw new Error(data.error || 'Failed to check the folder');
    return data.data;
  },

  /** Where the browser downloads an archive, from its path below `/api`. */
  backupDownloadUrl: (downloadPath: string): string => withServerUrl(`/api${downloadPath}`),

  getPixInsightSettings: async (): Promise<PixInsightSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<PixInsightSettings>>('/settings/pixinsight');
    if (!data.data) throw new Error(data.error || 'Failed to get PixInsight settings');
    return data.data;
  },

  /**
   * Names the PixInsight executable and the runs folder; null means the
   * standard places, and the export directory or cache, respectively.
   */
  updatePixInsightSettings: async (update: {
    binary: string | null;
    runs_dir: string | null;
  }): Promise<PixInsightSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<PixInsightSettings>>(
      '/settings/pixinsight',
      update
    );
    if (!data.data) throw new Error(data.error || 'Failed to update PixInsight settings');
    return data.data;
  },

  /** Target Scheduler rows its own migration left without a GUID. */
  getMissingGuids: async (dbId: string): Promise<GuidReport> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<GuidReport>>(dbPath(dbId, '/guids'));
    if (!data.data) throw new Error(data.error || 'Failed to count missing GUIDs');
    return data.data;
  },

  /** Copy the database beside itself, then give every such row a GUID. Management-gated server-side. */
  fillMissingGuids: async (dbId: string): Promise<GuidFillReport> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<GuidFillReport>>(dbPath(dbId, '/guids/fill'));
    if (!data.data) throw new Error(data.error || 'Failed to fill missing GUIDs');
    return data.data;
  },

  /** Lights catalogued twice because their file was renamed after it was imported. */
  getRenamedLights: async (dbId: string): Promise<RenamedLightsReport> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<RenamedLightsReport>>(dbPath(dbId, '/renamed-lights'));
    if (!data.data) throw new Error(data.error || 'Failed to check for renamed lights');
    return data.data;
  },

  /** Copy the database beside itself, then fold each second light into the first. */
  mergeRenamedLights: async (dbId: string): Promise<RenamedLightsMerged> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<RenamedLightsMerged>>(dbPath(dbId, '/renamed-lights/merge'));
    if (!data.data) throw new Error(data.error || 'Failed to merge renamed lights');
    return data.data;
  },

  /** Stack a project or target with WBPP on the server. Management-gated server-side. */
  startWbppRun: async (dbId: string, request: StartWbppRunRequest): Promise<WbppRunStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<WbppRunStatus>>(
      dbPath(dbId, '/wbpp/runs'),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to start the WBPP run');
    return data.data;
  },

  getWbppRun: async (dbId: string): Promise<WbppRunStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<WbppRunStatus>>(
      dbPath(dbId, '/wbpp/runs/current')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read the WBPP run');
    return data.data;
  },

  cancelWbppRun: async (dbId: string): Promise<WbppRunStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<WbppRunStatus>>(
      dbPath(dbId, '/wbpp/runs/current')
    );
    if (!data.data) throw new Error(data.error || 'Failed to stop the WBPP run');
    return data.data;
  },

  /** Clear a finished run from view; its folder stays on disk. */
  dismissWbppRun: async (dbId: string): Promise<WbppRunStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<WbppRunStatus>>(
      dbPath(dbId, '/wbpp/runs/current/dismiss')
    );
    if (!data.data) throw new Error(data.error || 'Failed to dismiss the WBPP run');
    return data.data;
  },

  /** Take a waiting run out of the line. */
  removeQueuedWbppRun: async (dbId: string, queueId: string): Promise<WbppRunStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<WbppRunStatus>>(
      dbPath(dbId, `/wbpp/runs/queue/${encodeURIComponent(queueId)}`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to remove the queued run');
    return data.data;
  },

  /** Save the finished run's masters to `<process_dir>/<folder>/master/`. */
  publishWbppRun: async (dbId: string, folder: string): Promise<WbppRunStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<WbppRunStatus>>(
      dbPath(dbId, '/wbpp/runs/current/publish'),
      { folder }
    );
    if (!data.data) throw new Error(data.error || 'Failed to save the masters');
    return data.data;
  },

  /** Absolute URL of a file the current WBPP run wrote, by its path below the run folder. */
  wbppRunFileUrl: (dbId: string, path: string): string =>
    withServerUrl(
      `/api${dbPath(dbId, '/wbpp/runs/current/files/')}${path
        .split('/')
        .map(encodeURIComponent)
        .join('/')}`
    ),

  getAstroBinSettings: async (): Promise<AstroBinSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AstroBinSettings>>('/settings/astrobin');
    if (!data.data) throw new Error(data.error || 'Failed to get AstroBin settings');
    return data.data;
  },

  /** Replaces the whole filter map; an entry left out is forgotten. */
  updateAstroBinSettings: async (
    filterIds: Record<string, number>
  ): Promise<AstroBinSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<AstroBinSettings>>('/settings/astrobin', {
      filter_ids: filterIds,
    });
    if (!data.data) throw new Error(data.error || 'Failed to update AstroBin settings');
    return data.data;
  },

  /** The catalog's own filter map: what each filter name meant on its rig, and when. */
  getAstroBinFilters: async (dbId: string): Promise<AstroBinFilterMap> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AstroBinFilterMap>>(
      dbPath(dbId, '/astrobin/filters')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read the AstroBin filter map');
    return data.data;
  },

  /** Replaces the catalog's whole filter map. Management-gated server-side. */
  updateAstroBinFilters: async (
    dbId: string,
    entries: AstroBinFilterEntry[]
  ): Promise<AstroBinFilterMap> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<AstroBinFilterMap>>(
      dbPath(dbId, '/astrobin/filters'),
      { entries }
    );
    if (!data.data) throw new Error(data.error || 'Failed to save the AstroBin filter map');
    return data.data;
  },

  /** The AstroBin acquisition rows for one target or project, with the CSV they render to. */
  getAstroBinExport: async (dbId: string, params: AstroBinExportParams): Promise<AstroBinExport> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AstroBinExport>>(
      dbPath(dbId, '/astrobin-export'),
      { params: astroBinQuery(params) }
    );
    if (!data.data) throw new Error(data.error || 'Failed to build the AstroBin export');
    return data.data;
  },

  /** Absolute URL for the AstroBin CSV download. */
  astroBinCsvUrl: (dbId: string, params: AstroBinExportParams): string => {
    const qs = new URLSearchParams(astroBinQuery(params)).toString();
    return withServerUrl(`/api${dbPath(dbId, '/astrobin-export.csv')}${qs ? `?${qs}` : ''}`);
  },

  getServerInfo: async (): Promise<ServerInfo> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ServerInfo>>('/info');
    if (!data.data) throw new Error('Failed to get server info');
    return data.data;
  },

  getUpdateNotice: async (): Promise<UpdateNoticeStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<UpdateNoticeStatus>>('/update-notice');
    if (!data.data) throw new Error('Failed to get update notice');
    return data.data;
  },

  getAstrometryCapabilities: async (): Promise<AstrometryCapabilities> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AstrometryCapabilities>>(
      '/astrometry/capabilities'
    );
    if (!data.data) throw new Error(data.error || 'Failed to inspect Seiza catalogs');
    return data.data;
  },

  getRcAstroCapabilities: async (): Promise<RcAstroCapabilities> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<RcAstroCapabilities>>('/tools/rc-astro');
    if (!data.data) throw new Error(data.error || 'Failed to probe RC-Astro tools');
    return data.data;
  },

  validateAstrometryCatalogs: async (): Promise<AstrometryValidationReport> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<AstrometryValidationReport>>(
      '/astrometry/catalogs/validate'
    );
    if (!data.data) throw new Error(data.error || 'Failed to validate Seiza catalogs');
    return data.data;
  },

  getCatalogInstallStatus: async (): Promise<CatalogInstallStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CatalogInstallStatus>>(
      '/astrometry/catalogs/install'
    );
    if (!data.data) throw new Error(data.error || 'Failed to get catalog install status');
    return data.data;
  },

  installCatalogs: async (preset: CatalogInstallPreset): Promise<CatalogInstallStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<CatalogInstallStatus>>(
      '/astrometry/catalogs/install',
      { preset }
    );
    if (!data.data) throw new Error(data.error || 'Failed to start catalog installation');
    return data.data;
  },

  /** Stop the star catalog installation under way. Catalogs installed
   *  before stay. */
  stopCatalogInstall: async (): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.post('/astrometry/catalogs/install/stop');
  },

  /** List every configured database. */
  getDatabases: async (): Promise<DatabaseSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<DatabaseSummary[]>>('/databases');
    return data.data || [];
  },

  /** Stop a preview under way. The request that started it fails with a
   *  conflict. */
  stopPreview: async (preview: import('./types').PreviewKind, key: string): Promise<void> => {
    const api = await getApi();
    await api.post(`/previews/${preview}/${encodeURIComponent(key)}/stop`);
  },

  stopTsSync: async (id: string): Promise<void> => {
    const api = await getApi();
    await api.post(`/ts-sync/${encodeURIComponent(id)}/stop`);
  },

  getTsSyncJobs: async (): Promise<import('./tsSyncTypes').TsSyncJob[]> => {
    const api = await getApi();
    const { data } = await api.get<ApiResponse<import('./tsSyncTypes').TsSyncJob[]>>('/ts-sync');
    return data.data || [];
  },

  importTsDatabase: async (request: { name: string; db_path: string; image_dirs: string[]; slug?: string; connect_source?: boolean; destination_path?: string }): Promise<import('./tsSyncTypes').TsSyncJob> => {
    const api = await getApi();
    const { data } = await api.post<ApiResponse<import('./tsSyncTypes').TsSyncJob>>('/databases/import-ts', request);
    if (!data.data) throw new Error(data.error || 'Could not start the Target Scheduler import.');
    return data.data;
  },

  /** Register a legacy database. New UI imports create an owned catalog. */
  addDatabase: async (req: {
    name: string;
    db_path: string;
    image_dirs: string[];
    slug?: string;
  }): Promise<DatabaseSummary> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<DatabaseSummary>>('/databases', req);
    if (!data.data) throw new Error(data.error || 'Failed to add database');
    return data.data;
  },

  /** Update an existing database. */
  updateDatabase: async (
    dbId: string,
    req: {
      ts_sync?: import('./tsSyncTypes').TsSyncSettings;
      name?: string;
      slug?: string;
      db_path?: string;
      image_dirs?: string[];
      remote_image_upload?: {
        enabled: boolean;
        image_directory?: string;
        token?: string;
        sync_enabled?: boolean;
        placement?: 'flat' | 'target_tree';
        directory_template?: string;
        /** `preset`: use `directory_template`; `catalog`: keep the catalog match (any template is ignored). Omit when the layout select was not touched. */
        directory_template_source?: RemoteImageUploadDirectoryTemplateSource;
        rescan_directory_layout?: boolean;
      };
      /** New export directory; empty string clears it. */
      export_dir?: string;
      /** New process directory; empty string clears it. */
      process_dir?: string;
      /** Replace the automatic import settings; the defaults (off) clear them. */
      autoimport?: AutoImportSettings;
      /** Replace the scan rules; rules that change nothing clear them. */
      scan_rules?: ScanRules;
    }
  ): Promise<DatabaseSummary> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<DatabaseSummary>>(
      `/databases/${encodeURIComponent(dbId)}`,
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to update database');
    return data.data;
  },

  /** Remove a database by slug. */
  removeDatabase: async (dbId: string): Promise<boolean> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<{ removed: boolean }>>(
      `/databases/${encodeURIComponent(dbId)}`
    );
    return data.data?.removed ?? false;
  },

  /** Preview or run a safe scheduler database sync. */
  syncDatabase: async (
    dbId: string,
    req: SchedulerSyncRequest
  ): Promise<SchedulerSyncResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<SchedulerSyncResponse>>(
      `/databases/${encodeURIComponent(dbId)}/sync`,
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to sync databases');
    return data.data;
  },

  /** Remote PSF Guard peers this instance can sync with. */
  getPeers: async (): Promise<PeerSummary[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<PeerSummary[]>>('/peers');
    return data.data ?? [];
  },

  addPeer: async (req: {
    name: string;
    base_url: string;
    token: string;
    catalog_id?: string | null;
  }): Promise<PeerSummary> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<PeerSummary>>('/peers', req);
    if (!data.data) throw new Error(data.error || 'Failed to add peer');
    return data.data;
  },

  /** Omit `token` to keep the stored key: the browser never receives it. */
  updatePeer: async (
    peerId: string,
    req: { name: string; base_url: string; token?: string; catalog_id?: string | null }
  ): Promise<PeerSummary> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<PeerSummary>>(
      `/peers/${encodeURIComponent(peerId)}`,
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to update peer');
    return data.data;
  },

  removePeer: async (peerId: string): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.delete(`/peers/${encodeURIComponent(peerId)}`);
  },

  /** Ask a peer who it is. An unreachable peer is a normal answer. */
  checkPeer: async (peerId: string): Promise<PeerCheck> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<PeerCheck>>(
      `/peers/${encodeURIComponent(peerId)}/check`
    );
    if (!data.data) throw new Error(data.error || 'Failed to reach peer');
    return data.data;
  },

  /** Sync one local catalog with a peer over HTTP. Dry run unless told otherwise. */
  syncWithPeer: async (
    dbId: string,
    req: RemoteSyncRequest
  ): Promise<RemoteSyncResult> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<RemoteSyncResult>>(
      `/databases/${encodeURIComponent(dbId)}/sync/remote`,
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to sync with the peer');
    return data.data;
  },

  /** Create a server-owned dry preview that must be applied by its ID. */
  previewDatabaseSync: async (
    dbId: string,
    req: SchedulerSyncRequest
  ): Promise<SchedulerSyncPreviewResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<SchedulerSyncPreviewResponse>>(
      `/databases/${encodeURIComponent(dbId)}/sync/preview`,
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to preview database sync');
    return data.data;
  },

  /** Reload one durable preview after the Settings UI closes or reloads. */
  getDatabaseSyncPreview: async (
    dbId: string,
    previewId: string
  ): Promise<SchedulerSyncPreviewResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<SchedulerSyncPreviewResponse>>(
      `/databases/${encodeURIComponent(dbId)}/sync/previews/${encodeURIComponent(previewId)}`
    );
    if (!data.data) throw new Error(data.error || 'Database sync preview not found');
    return data.data;
  },

  /** Mint a one-time pairing code for a catalog. The client exchanges it
   * once at /api/sync/v1/pair for the durable bearer token. */
  issuePairingCode: async (
    dbId: string
  ): Promise<{ pairing_token: string; expires_at: number }> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<
      ApiResponse<{ pairing_token: string; expires_at: number }>
    >(`/databases/${encodeURIComponent(dbId)}/pairing-token`);
    if (!data.data) throw new Error(data.error || 'Failed to issue pairing code');
    return data.data;
  },

  /** Revoke one paired client's credential. Other clients keep working. */
  revokePairedClient: async (dbId: string, clientUuid: string): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.delete(
      `/databases/${encodeURIComponent(dbId)}/clients/${encodeURIComponent(clientUuid)}`
    );
  },

  /** Every unexpired staged preview for one catalog, including previews a
   * remote client (the N.I.N.A. plugin) parked and never applied. */
  listDatabaseSyncPreviews: async (
    dbId: string
  ): Promise<SchedulerSyncPreviewListEntry[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<SchedulerSyncPreviewListEntry[]>>(
      `/databases/${encodeURIComponent(dbId)}/sync/previews`
    );
    return data.data ?? [];
  },

  /** Re-run one preview's dry run against the catalog as it stands now. */
  refreshDatabaseSyncPreview: async (
    dbId: string,
    previewId: string
  ): Promise<SchedulerSyncPreviewResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<SchedulerSyncPreviewResponse>>(
      `/databases/${encodeURIComponent(dbId)}/sync/previews/${encodeURIComponent(previewId)}/refresh`
    );
    if (!data.data) throw new Error(data.error || 'Failed to refresh database sync preview');
    return data.data;
  },

  deleteDatabaseSyncPreview: async (dbId: string, previewId: string): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.delete(
      `/databases/${encodeURIComponent(dbId)}/sync/previews/${encodeURIComponent(previewId)}`
    );
  },

  /** Apply one unexpired server-owned preview. */
  applyDatabaseSyncPreview: async (
    dbId: string,
    previewId: string
  ): Promise<SchedulerSyncResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<SchedulerSyncResponse>>(
      `/databases/${encodeURIComponent(dbId)}/sync/previews/${encodeURIComponent(previewId)}/apply`
    );
    if (!data.data) throw new Error(data.error || 'Failed to apply database sync preview');
    return data.data;
  },

  /**
   * Create a brand-new Target Scheduler database (full upstream schema) and
   * start a background import of the given FITS folders.
   */
  createDatabaseFromImages: async (
    req: CreateDatabaseRequest
  ): Promise<CreateDatabaseResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<CreateDatabaseResponse>>(
      '/databases/create',
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to create database');
    return data.data;
  },

  /** Start a background FITS import into an existing database. */
  startImport: async (dbId: string, req: ImportRequest): Promise<ImportStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ImportStatus>>(
      dbPath(dbId, '/import'),
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to start import');
    return data.data;
  },

  /** Calibration coverage for one project's lights. */
  getCalibrationHealth: async (dbId: string): Promise<import('./types').CalibrationHealthList> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<import('./types').CalibrationHealthList>>(
      dbPath(dbId, '/calibration-health')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration health');
    return data.data;
  },

  /** The lights each calibration health result found something in, for
   *  one project or, without one, the whole database. */
  getCalibrationLightMarks: async (
    dbId: string,
    projectId: number | null
  ): Promise<import('./types').CalibrationLightMarks> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<import('./types').CalibrationLightMarks>>(
      dbPath(dbId, '/calibration-health/lights'),
      { params: projectId == null ? undefined : { project_id: projectId } }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration marks');
    return data.data;
  },

  /** Which frames calibrate one light, and what the health check finds. */
  getImageCalibration: async (
    dbId: string,
    imageId: number,
    /** The view's night split over the server's setting (useNightSplit). */
    nights?: 'rig' | 'catalog' | null,
  ): Promise<import('./types').ImageCalibrationExplanation> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<
      ApiResponse<import('./types').ImageCalibrationExplanation>
    >(dbPath(dbId, `/images/${imageId}/calibration`), { params: nights ? { nights } : undefined });
    if (!data.data) throw new Error(data.error || 'Failed to read the light\'s calibration');
    return data.data;
  },

  getProjectCalibrationHealth: async (
    dbId: string,
    projectId: number
  ): Promise<import('./types').ProjectCalibrationHealthResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<
      ApiResponse<import('./types').ProjectCalibrationHealthResponse>
    >(dbPath(dbId, `/projects/${projectId}/calibration-health`));
    if (!data.data) throw new Error(data.error || 'Failed to read calibration health');
    return data.data;
  },

  /** Rejected lights are left out unless `includeRejected` is set. */
  getProjectCalibrationReport: async (
    dbId: string,
    projectId: number,
    includeRejected = false,
    /** The view's night split over the server's setting (useNightSplit). */
    nights?: 'rig' | 'catalog' | null,
  ): Promise<import('./types').ProjectCalibrationReport> => {
    const apiInstance = await getApi();
    const params: Record<string, string | boolean> = {};
    if (includeRejected) params.include_rejected = true;
    if (nights) params.nights = nights;
    const { data } = await apiInstance.get<
      ApiResponse<import('./types').ProjectCalibrationReport>
    >(dbPath(dbId, `/projects/${projectId}/calibration-report`), {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    if (!data.data) throw new Error(data.error || 'Failed to build the calibration report');
    return data.data;
  },

  /** Every light of the project a stack leaves out because the library
   *  cannot calibrate it, with a digest naming the list. */
  getProjectCalibrationGaps: async (
    dbId: string,
    projectId: number,
    nights?: 'rig' | 'catalog' | null,
  ): Promise<import('./types').ProjectCalibrationGaps> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<import('./types').ProjectCalibrationGaps>>(
      dbPath(dbId, `/projects/${projectId}/calibration-report/rejects`),
      { params: nights ? { nights } : undefined },
    );
    if (!data.data) throw new Error(data.error || 'Failed to check the lights');
    return data.data;
  },

  /** Reject the lights the check listed; 409 when the list changed since. */
  rejectUncalibratedLights: async (dbId: string, projectId: number, digest: string): Promise<BatchGradeResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<BatchGradeResponse>>(
      dbPath(dbId, `/projects/${projectId}/calibration-report/rejects`),
      { digest }
    );
    if (!data.data) throw new Error(data.error || 'Failed to reject the lights');
    return data.data;
  },

  /** The configured image roots and two levels of their subdirectories. */
  getImportFolders: async (dbId: string): Promise<import('./types').ImportFolder[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<import('./types').ImportFolder[]>>(
      dbPath(dbId, '/import/folders')
    );
    if (!data.data) throw new Error(data.error || 'Failed to list import folders');
    return data.data;
  },

  /** Import job progress (poll ~1s while running). */
  getImportStatus: async (dbId: string): Promise<ImportStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ImportStatus>>(dbPath(dbId, '/import'));
    if (!data.data) throw new Error('Failed to get import status');
    return data.data;
  },

  /** The automatic import's settings, schedule, and last run. */
  getAutoImportStatus: async (dbId: string): Promise<AutoImportStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AutoImportStatus>>(
      dbPath(dbId, '/autoimport')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read automatic import status');
    return data.data;
  },

  /** Run the automatic import now, with its configured scope. */
  runAutoImport: async (dbId: string): Promise<ImportStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ImportStatus>>(
      dbPath(dbId, '/autoimport/run')
    );
    if (!data.data) throw new Error(data.error || 'Failed to start the import');
    return data.data;
  },

  /** Calibration frames and generated masters cataloged beside one database. */
  getCalibrationLibrary: async (dbId: string): Promise<CalibrationLibrarySummary> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibrationLibrarySummary>>(
      dbPath(dbId, '/calibrations')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration library');
    return data.data;
  },

  /** One page of calibration frames the filters keep. */
  getCalibrationFrames: async (
    dbId: string,
    filters: CalibrationListFilters
  ): Promise<CalibrationFramePage> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibrationFramePage>>(
      dbPath(dbId, '/calibrations/frames'),
      { params: filters }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration frames');
    return data.data;
  },

  /** One page of the sessions holding frames the filters keep. */
  getCalibrationSessions: async (
    dbId: string,
    filters: CalibrationListFilters
  ): Promise<CalibrationSessionPage> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibrationSessionPage>>(
      dbPath(dbId, '/calibrations/sessions'),
      { params: filters }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration sessions');
    return data.data;
  },

  /** What one project's lights use from the calibration library. */
  getCalibrationProjectScope: async (dbId: string, project: number): Promise<CalibrationProjectScope> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibrationProjectScope>>(
      dbPath(dbId, '/calibrations/project-scope'),
      { params: { project } }
    );
    if (!data.data) throw new Error(data.error || "Failed to read the project's calibration");
    return data.data;
  },

  /** One frame: its measurement, findings and its session's numbers. */
  getCalibrationFrame: async (dbId: string, frameUuid: string): Promise<CalibrationFrameDetail> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibrationFrameDetail>>(
      dbPath(dbId, `/calibrations/frames/${encodeURIComponent(frameUuid)}`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to read the calibration frame');
    return data.data;
  },

  /** A picture of one raw calibration frame; 202 while it is drawn. */
  getCalibrationFramePreviewUrl: (
    dbId: string,
    frameUuid: string,
    options: { view: CalibrationFrameView; size: CalibrationFrameSize; stretch: CalibrationFrameStretch }
  ): string => {
    const params = new URLSearchParams({
      view: options.view,
      size: options.size,
      stretch: options.stretch,
    });
    return withServerUrl(
      `/api${dbPath(dbId, `/calibrations/frames/${encodeURIComponent(frameUuid)}/preview`)}?${params}`
    );
  },

  /** How each rig's flats group by measurement, with the changes between groups. */
  getFlatGroups: async (dbId: string, project?: number): Promise<FlatGroupsView> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<FlatGroupsView>>(
      dbPath(dbId, '/calibrations/flat-groups'),
      { params: project === undefined ? {} : { project } }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read the flat groups');
    return data.data;
  },

  /** Split a family's groups at a session, join two neighbouring groups,
   *  or reset either. Behind the database-management gate. */
  setFlatGroupOverride: async (
    dbId: string,
    request: {
      choice: 'split' | 'join' | 'reset';
      after_response?: string;
      before_response?: string;
      anchor_frame?: string;
    }
  ): Promise<{ anchor_frame: string; choice: 'split' | 'join' | null }> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<
      ApiResponse<{ anchor_frame: string; choice: 'split' | 'join' | null }>
    >(dbPath(dbId, '/calibrations/flat-groups/overrides'), request);
    if (!data.data) throw new Error(data.error || 'Failed to change the flat groups');
    return data.data;
  },

  /** Put lights before or after a change, give them a group's flats, or
   *  reset their choices. Behind the database-management gate. */
  setFlatLightChoice: async (
    dbId: string,
    request: {
      image_ids: number[];
      whole_session?: boolean;
      choice: 'before' | 'after' | 'group' | 'reset';
      change_key?: string;
      group_response?: string;
    }
  ): Promise<{ lights: number; without_guid: number; skipped: number }> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<
      ApiResponse<{ lights: number; without_guid: number; skipped: number }>
    >(
      dbPath(dbId, '/calibrations/flat-choices'),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to set the flat choice');
    return data.data;
  },

  /** The changes a set of lights were shot during. */
  getFlatLightChoiceOptions: async (
    dbId: string,
    imageIds: number[]
  ): Promise<FlatLightChoiceOptions> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<FlatLightChoiceOptions>>(
      dbPath(dbId, '/calibrations/flat-choices/options'),
      { image_ids: imageIds }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read the flat changes');
    return data.data;
  },

  /** Where a flat session would sit among the flat groups, measured or not. */
  getFlatSessionPlace: async (dbId: string, session: string): Promise<FlatSessionPlace> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<FlatSessionPlace>>(
      dbPath(dbId, '/calibrations/flat-groups/place'),
      { params: { session } }
    );
    if (!data.data) throw new Error(data.error || 'Failed to place the flat session');
    return data.data;
  },

  /** What each flat group feeds: its flat masters and the lights that take it. */
  getFlatGroupUse: async (dbId: string, group?: string): Promise<FlatGroupUseView> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<FlatGroupUseView>>(
      dbPath(dbId, '/calibrations/flat-groups/use'),
      { params: group === undefined ? {} : { group } }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read what the flat groups feed');
    return data.data;
  },

  /** The ratio picture of two flat sessions, later ÷ earlier; 202 while it is drawn. */
  getFlatRatioUrl: (
    dbId: string,
    options: { earlier: string; later: string; stretch: CalibrationFrameStretch }
  ): string => {
    const params = new URLSearchParams({
      earlier: options.earlier,
      later: options.later,
      stretch: options.stretch,
    });
    return withServerUrl(`/api${dbPath(dbId, '/calibrations/flat-groups/ratio')}?${params}`);
  },

  /** The calibration quality job's progress. */
  /** Where flat measuring stands. The header and the page share this poll. */
  getFlatMeasureStatus: async (dbId: string): Promise<FlatMeasureStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<FlatMeasureStatus>>(
      dbPath(dbId, '/calibrations/flat-measure')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read flat measuring progress');
    return data.data;
  },

  /** Say a page shows this rig's flats: its pass goes first, in the viewed
   *  tier, for a while after each call. */
  watchFlatMeasure: async (dbId: string, rig: string): Promise<FlatMeasureStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<FlatMeasureStatus>>(
      dbPath(dbId, '/calibrations/flat-measure/watch'),
      { rig }
    );
    if (!data.data) throw new Error(data.error || 'Failed to note the rig in view');
    return data.data;
  },

  /** Pause one of the database's automatic jobs until Resume or until the
   *  server restarts. Resolves to false when it was paused already. */
  pauseJob: async (dbId: string, job: PausableJob): Promise<boolean> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<boolean>>(dbPath(dbId, `/jobs/${job}/pause`));
    if (data.data == null) throw new Error(data.error || 'Failed to pause the job');
    return data.data;
  },

  /** Resume a paused job; the work it held starts. Resolves to false when
   *  it was not paused. */
  resumeJob: async (dbId: string, job: PausableJob): Promise<boolean> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<boolean>>(dbPath(dbId, `/jobs/${job}/resume`));
    if (data.data == null) throw new Error(data.error || 'Failed to resume the job');
    return data.data;
  },

  /** Stop the database's export before its next file. The files placed
   *  stay, and the next export skips them. */
  stopServerExport: async (dbId: string): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.post(dbPath(dbId, '/export/server/stop'));
  },

  /** Stop the database's import or import preview. An import that is
   *  writing rolls back. */
  stopImport: async (dbId: string): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.post(dbPath(dbId, '/import/stop'));
  },

  /** Where reading frame sites stands, for the job line. */
  getFrameSitesStatus: async (dbId: string): Promise<FrameSitesStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<FrameSitesStatus>>(
      dbPath(dbId, '/calibrations/frame-sites')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read frame site progress');
    return data.data;
  },

  getCalibrationQuality: async (dbId: string): Promise<CalibrationQualityStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibrationQualityStatus>>(
      dbPath(dbId, '/calibrations/quality')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration grading progress');
    return data.data;
  },

  /** Every master the library recorded, in the inspector's shape. */
  getLibraryCalibrationMasters: async (dbId: string, project?: number): Promise<LibraryCalibrationMasters> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<LibraryCalibrationMasters>>(
      dbPath(dbId, '/calibrations/masters'),
      { params: project === undefined ? {} : { project } }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration masters');
    return {
      ...data.data,
      masters: data.data.masters.map((master) => ({
        ...master,
        preview_url: master.preview_url ? withServerUrl(master.preview_url) : null,
        original_preview_url: master.original_preview_url ? withServerUrl(master.original_preview_url) : null,
        fits_url: master.fits_url ? withServerUrl(master.fits_url) : null,
      })),
    };
  },

  getSchedulerFlatHistory: async (
    dbId: string,
    params: { limit: number; offset: number; state?: FlatHistoryState; q?: string }
  ): Promise<SchedulerFlatHistoryPage> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<SchedulerFlatHistoryPage>>(
      dbPath(dbId, '/flat-history'), { params }
    );
    if (!data.data) throw new Error(data.error || 'Failed to read scheduler flat coverage');
    return data.data;
  },

  invalidateSchedulerFlatHistory: async (
    dbId: string, recordIds: string[], reason: string
  ): Promise<{ invalidated: number }> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<{ invalidated: number }>>(
      dbPath(dbId, '/flat-history/invalidate'), { record_ids: recordIds, reason }
    );
    if (!data.data) throw new Error(data.error || 'Failed to invalidate flat coverage');
    return data.data;
  },

  setCalibrationQualityOverride: async (
    dbId: string,
    frameUuids: string[],
    choice: 'use' | 'exclude' | 'clear'
  ): Promise<{ updated: number }> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<{ updated: number }>>(
      dbPath(dbId, '/calibrations/frames/quality'),
      { frame_uuids: frameUuids, choice }
    );
    if (!data.data) throw new Error(data.error || 'Failed to set the frame choice');
    return data.data;
  },

  setCalibrationValidity: async (
    dbId: string,
    frameUuids: string[],
    direction: 'both' | 'forward' | 'backward'
  ): Promise<{ updated: number }> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<{ updated: number }>>(
      dbPath(dbId, '/calibrations/frames/validity'),
      { frame_uuids: frameUuids, direction }
    );
    if (!data.data) throw new Error(data.error || 'Failed to mark calibration validity');
    return data.data;
  },

  forgetCalibrationFrames: async (
    dbId: string,
    frameUuids: string[]
  ): Promise<CalibrationMutationOutcome> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<CalibrationMutationOutcome>>(
      dbPath(dbId, '/calibrations/frames'),
      { data: { frame_uuids: frameUuids } }
    );
    if (!data.data) throw new Error(data.error || 'Failed to forget calibration frames');
    return data.data;
  },

  forgetCalibrationFrame: async (
    dbId: string,
    frameUuid: string
  ): Promise<CalibrationMutationOutcome> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<CalibrationMutationOutcome>>(
      dbPath(dbId, `/calibrations/frames/${encodeURIComponent(frameUuid)}`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to forget calibration frame');
    return data.data;
  },

  /** Where the duplicate check or repair stands, with the latest plan:
   *  exposures the library records more than once. Does no work. */
  getCalibrationDuplicates: async (dbId: string): Promise<CalibrationDuplicateStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibrationDuplicateStatus>>(
      dbPath(dbId, '/calibrations/duplicates')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read calibration duplicates');
    return data.data;
  },

  /** Start a check for duplicates (or join the one running); poll the
   *  status for its plan. `automatic` when the page starts it on its own:
   *  it runs in the background and waits for a person's work. */
  checkCalibrationDuplicates: async (dbId: string, automatic = false): Promise<CalibrationDuplicateStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<CalibrationDuplicateStatus>>(
      dbPath(dbId, `/calibrations/duplicates/check${automatic ? '?automatic=true' : ''}`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to check for calibration duplicates');
    return data.data;
  },

  /** Start the previewed repair; poll the status. It applies nothing when
   *  the plan has changed, and says what changed. */
  repairCalibrationDuplicates: async (dbId: string, token: string): Promise<CalibrationDuplicateStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<CalibrationDuplicateStatus>>(
      dbPath(dbId, '/calibrations/duplicates/repair'),
      { token }
    );
    if (!data.data) throw new Error(data.error || 'Failed to repair calibration duplicates');
    return data.data;
  },

  /** Stop the check or repair running: a repair rolls back whole. */
  stopCalibrationDuplicates: async (dbId: string): Promise<CalibrationDuplicateStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<CalibrationDuplicateStatus>>(
      dbPath(dbId, '/calibrations/duplicates/stop')
    );
    if (!data.data) throw new Error(data.error || 'Failed to stop the duplicate check');
    return data.data;
  },

  clearCalibrationMasters: async (dbId: string): Promise<CalibrationMutationOutcome> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<CalibrationMutationOutcome>>(
      dbPath(dbId, '/calibrations/masters')
    );
    if (!data.data) throw new Error(data.error || 'Failed to clear calibration masters');
    return data.data;
  },

  /** Absolute URL for the streaming light and calibration export zip. */
  exportDownloadUrl: (
    dbId: string,
    params: {
      project_id?: number;
      target_id?: number;
      include_pending?: boolean;
      layout?: ExportLayout;
      /** `reference` downloads only the WBPP runner scripts, which name the frames where they are. */
      placement?: import('./types').ExportPlacement;
      local_root?: string;
      remote_root?: string;
      /** The WBPP settings the runner passes; sent only with the WBPP layout. */
      wbpp?: WbppOptions;
    }
  ): string => {
    const query = new URLSearchParams();
    if (params.project_id !== undefined) query.set('project_id', String(params.project_id));
    if (params.target_id !== undefined) query.set('target_id', String(params.target_id));
    if (params.include_pending) query.set('include_pending', 'true');
    if (params.layout && params.layout !== 'standard') query.set('layout', params.layout);
    if (params.layout === 'wbpp' && params.wbpp) {
      query.set('wbpp_quality', params.wbpp.quality);
      query.set('wbpp_fast_integration', params.wbpp.fast_integration);
      query.set('wbpp_drizzle', params.wbpp.drizzle);
      if (params.wbpp.autocrop !== undefined) query.set('wbpp_autocrop', String(params.wbpp.autocrop));
      if (params.wbpp.rejection) query.set('wbpp_rejection', params.wbpp.rejection);
    }
    if (params.placement === 'reference') {
      query.set('placement', 'reference');
      if (params.local_root) query.set('local_root', params.local_root);
      if (params.remote_root) query.set('remote_root', params.remote_root);
    }
    const qs = query.toString();
    return withServerUrl(`/api${dbPath(dbId, '/export')}${qs ? `?${qs}` : ''}`);
  },

  /**
   * Run the folder export on the server's own filesystem (desktop mode:
   * server and user share a machine). Hardlinks by default, falling back to
   * copy. Management-gated server-side.
   */
  exportLocal: async (
    dbId: string,
    req: {
      dest: string;
      project_id?: number;
      target_id?: number;
      include_pending?: boolean;
      filter_name?: string;
      layout?: ExportLayout;
      link?: boolean;
      placement?: import('./types').ExportPlacement;
      local_root?: string;
      remote_root?: string;
      dry_run?: boolean;
    }
  ): Promise<import('./types').ExportSummary> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').ExportSummary>>(
      dbPath(dbId, '/export/local'),
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to export');
    return data.data;
  },

  /**
   * Start the background export into the server's configured export
   * directory. Reflinks where the filesystem supports it, copies elsewhere.
   * Only offered when the database reports an `export_directory`.
   */
  startServerExport: async (
    dbId: string,
    req: {
      project_id?: number;
      target_id?: number;
      include_pending?: boolean;
      filter_name?: string;
      layout?: ExportLayout;
      subdirectory?: string;
      scope_label?: string;
      placement?: import('./types').ExportPlacement;
      local_root?: string;
      remote_root?: string;
    }
  ): Promise<import('./types').ExportStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').ExportStatus>>(
      dbPath(dbId, '/export/server'),
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to start the export');
    return data.data;
  },

  getServerExportStatus: async (dbId: string): Promise<import('./types').ExportStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<import('./types').ExportStatus>>(
      dbPath(dbId, '/export/server')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read export progress');
    return data.data;
  },

  /** Update a project's Target Scheduler fields. */
  updateProject: async (
    dbId: string,
    projectId: number,
    request: ProjectSchedulerUpdate | string
  ): Promise<void> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<{ updated: boolean }>>(
      dbPath(dbId, `/projects/${projectId}`),
      typeof request === 'string' ? { name: request } : request
    );
    if (!data.data) throw new Error(data.error || 'Failed to update project');
  },

  /** Rename a target and/or move it to another project (same profile). */
  updateTarget: async (
    dbId: string,
    targetId: number,
    req: TargetSchedulerUpdate
  ): Promise<void> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<{ updated: boolean }>>(
      dbPath(dbId, `/targets/${targetId}`),
      req
    );
    if (!data.data) throw new Error(data.error || 'Failed to update target');
  },

  /** Merge a project's targets and images into another project. */
  mergeProject: async (
    dbId: string,
    projectId: number,
    intoProjectId: number
  ): Promise<{ targets_moved: number; images_moved: number }> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<
      ApiResponse<{ targets_moved: number; images_moved: number }>
    >(dbPath(dbId, `/projects/${projectId}/merge`), { into_project_id: intoProjectId });
    if (!data.data) throw new Error(data.error || 'Failed to merge project');
    return data.data;
  },

  /** What deleting a project would take from its catalog. */
  getProjectRemoval: async (dbId: string, projectId: number): Promise<ProjectRemovalPreview> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ProjectRemovalPreview>>(
      dbPath(dbId, `/projects/${projectId}/removal`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to read what deleting the project removes');
    return data.data;
  },

  /** Delete a project as previewed; a changed project answers with a new preview. */
  removeProject: async (dbId: string, projectId: number, digest: string): Promise<ProjectRemovalOutcome> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ProjectRemovalOutcome>>(
      dbPath(dbId, `/projects/${projectId}/removal`),
      { digest }
    );
    if (!data.data) throw new Error(data.error || 'Failed to delete the project');
    return data.data;
  },

  /** Deleted projects that syncs and imports leave out, newest first. */
  getDeletedProjects: async (dbId: string): Promise<DeletedProject[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<DeletedProject[]>>(dbPath(dbId, '/deleted-projects'));
    if (!data.data) throw new Error(data.error || 'Failed to list deleted projects');
    return data.data;
  },

  /** Let the next sync or import add a deleted project back. */
  allowDeletedProjectBack: async (dbId: string, deletionId: number): Promise<void> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<{ allowed: boolean }>>(
      dbPath(dbId, `/deleted-projects/${deletionId}/allow`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to allow the project back');
  },

  getOrganizationDestinations: async (dbId: string): Promise<OrganizationDestinations> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<OrganizationDestinations>>(
      dbPath(dbId, '/organization/destinations'),
    );
    if (!data.data) throw new Error(data.error || 'Could not load destinations');
    return data.data;
  },

  previewOrganization: async (
    dbId: string,
    operation: OrganizationOperation,
  ): Promise<OrganizationPreview> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<OrganizationPreview>>(
      dbPath(dbId, '/organization/preview'), operation,
    );
    if (!data.data) throw new Error(data.error || 'Could not preview the change');
    return data.data;
  },

  applyOrganization: async (
    dbId: string,
    operation: OrganizationOperation,
    expectedFingerprint: string,
  ): Promise<OrganizationResult> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<OrganizationResult>>(
      dbPath(dbId, '/organization/apply'),
      { operation, expected_fingerprint: expectedFingerprint },
    );
    if (!data.data) throw new Error(data.error || 'Could not apply the change');
    return data.data;
  },

  // ── Per-DB ────────────────────────────────────────────────────────────────

  refreshFileCache: async (dbId: string): Promise<FileCheckResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<FileCheckResponse>>(
      dbPath(dbId, '/refresh-cache')
    );
    if (!data.data) throw new Error('Failed to refresh cache');
    return data.data;
  },

  refreshDirectoryCache: async (dbId: string): Promise<DirectoryTreeResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<DirectoryTreeResponse>>(
      dbPath(dbId, '/refresh-directory-cache')
    );
    if (!data.data) throw new Error('Failed to refresh directory cache');
    return data.data;
  },

  getProjects: async (dbId: string): Promise<Project[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<Project[]>>(dbPath(dbId, '/projects'));
    return data.data || [];
  },

  getTargets: async (dbId: string, projectId: number): Promise<Target[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<Target[]>>(
      dbPath(dbId, `/projects/${projectId}/targets`)
    );
    return data.data || [];
  },

  /** The project's mosaic panels, or `null` when it is not a mosaic. */
  getProjectMosaic: async (dbId: string, projectId: number): Promise<ProjectMosaic | null> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<{ mosaic: ProjectMosaic | null }>>(
      dbPath(dbId, `/projects/${projectId}/mosaic`)
    );
    return data.data?.mosaic ?? null;
  },

  getTargetNavigation: async (dbId: string): Promise<TargetNavigation[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<TargetNavigation[]>>(
      dbPath(dbId, '/targets')
    );
    return data.data || [];
  },

  /** Global across every database, so no dbId parameter. */
  getProcessingSetups: async (): Promise<ProcessingSetupsDocument> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ProcessingSetupsDocument>>(
      '/processing-setups'
    );
    return data.data ?? { schema_version: 1, setups: [] };
  },

  saveProcessingSetup: async (
    name: string,
    kind: ProcessingSetupKind,
    settings: unknown
  ): Promise<ProcessingSetup> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ProcessingSetup>>(
      '/processing-setups',
      { name, kind, settings }
    );
    if (!data.data) throw new Error(data.error || 'Failed to save the processing setup');
    return data.data;
  },

  deleteProcessingSetup: async (name: string): Promise<ProcessingSetupsDocument> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.delete<ApiResponse<ProcessingSetupsDocument>>(
      `/processing-setups/${encodeURIComponent(name)}`
    );
    if (!data.data) throw new Error(data.error || 'Failed to delete the processing setup');
    return data.data;
  },

  importProcessingSetups: async (
    document: ProcessingSetupsDocument
  ): Promise<ProcessingSetupsImportResult> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ProcessingSetupsImportResult>>(
      '/processing-setups/import',
      document
    );
    if (!data.data) throw new Error(data.error || 'Failed to import processing setups');
    return data.data;
  },

  getProjectProcessingSettings: async (
    dbId: string, projectId: number, signal?: AbortSignal
  ): Promise<ProjectProcessingSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ProjectProcessingSettings>>(
      dbPath(dbId, `/projects/${projectId}/processing-settings`), { signal }
    );
    if (!data.data) throw new Error(data.error || 'Project processing settings could not be loaded');
    return data.data;
  },

  updateProjectProcessingSettings: async (
    dbId: string, projectId: number, settings: ProjectProcessingSettings
  ): Promise<ProjectProcessingSettings> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<ProjectProcessingSettings>>(
      dbPath(dbId, `/projects/${projectId}/processing-settings`), settings
    );
    if (!data.data) throw new Error(data.error || 'Project processing settings could not be saved');
    return data.data;
  },

  startStackPreviews: async (
    dbId: string,
    projectId: number,
    request: StackPreviewStartRequest
  ): Promise<StackPreviewJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<StackPreviewJob>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews`),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to start stack previews');
    return data.data;
  },
  /**
   * The same request split into one build per channel, queued in channel
   * order; channels already built for it come back as they are.
   */
  startStackPreviewChannels: async (
    dbId: string,
    projectId: number,
    request: StackPreviewStartRequest
  ): Promise<StackPreviewJob[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<StackPreviewJob[]>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/channels`),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to start stack previews');
    return data.data;
  },

  getStackPreviewJob: async (
    dbId: string,
    projectId: number,
    jobId: string
  ): Promise<StackPreviewJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StackPreviewJob>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/${encodeURIComponent(jobId)}`)
    );
    if (!data.data) throw new Error(data.error || 'Stack preview job not found');
    return data.data;
  },

  cancelStackPreviewJob: async (
    dbId: string,
    projectId: number,
    jobId: string
  ): Promise<StackPreviewJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<StackPreviewJob>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/${encodeURIComponent(jobId)}/cancel`)
    );
    if (!data.data) throw new Error(data.error || 'Stack build could not be stopped');
    return data.data;
  },

  getStackActivity: async (): Promise<StackActivity> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StackActivity>>('/stack-activity');
    return data.data ?? { schema_version: 1, active: [] };
  },

  /** Move a waiting stack build to `position` in the line (0 is next). */
  moveStackJob: async (jobId: string, position: number): Promise<StackActivity> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<StackActivity>>(
      `/stack-activity/${encodeURIComponent(jobId)}/move`,
      { position }
    );
    if (!data.data) throw new Error(data.error || 'Failed to move the build');
    return data.data;
  },

  /** Stop a mono or color stack build, waiting or running. */
  cancelStackJob: async (jobId: string): Promise<StackActivity> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<StackActivity>>(
      `/stack-activity/${encodeURIComponent(jobId)}/cancel`
    );
    if (!data.data) throw new Error(data.error || 'Failed to stop the build');
    return data.data;
  },

  /** Every running, queued and waiting job on the server, as the header lists them. */
  getJobs: async (): Promise<JobsResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<JobsResponse>>('/jobs');
    return data.data ?? { jobs: [], databases: [] };
  },

  getWbppActivity: async (): Promise<WbppActivity> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<WbppActivity>>('/wbpp/activity');
    return data.data ?? { running: [], queued: [] };
  },

  /** Move a waiting WBPP run to `position` in the line (0 is next). */
  moveQueuedWbppRun: async (queueId: string, position: number): Promise<WbppActivity> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<WbppActivity>>(
      `/wbpp/queue/${encodeURIComponent(queueId)}/move`,
      { position }
    );
    if (!data.data) throw new Error(data.error || 'Failed to move the queued run');
    return data.data;
  },

  /** The stacks WBPP made for a project and PSF Guard took in. */
  getWbppStacks: async (dbId: string, projectId: number): Promise<LatestStackPreviews> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<LatestStackPreviews>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/wbpp`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to load WBPP stacks');
    return data.data;
  },

  /** Take a WBPP run's master lights in as stacks: the last run by default. */
  importWbppStacks: async (
    dbId: string,
    projectId: number,
    request: { target_id?: number } = {}
  ): Promise<WbppStackImport> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<WbppStackImport>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/wbpp/import`),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to take in the WBPP masters');
    return data.data;
  },

  getLatestStackPreviews: async (
    dbId: string,
    projectId: number
  ): Promise<LatestStackPreviews> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<LatestStackPreviews>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/latest`)
    );
    if (!data.data) throw new Error(data.error || 'Latest stack previews could not be loaded');
    return data.data;
  },

  getStackPreviewUrl: (
    dbId: string,
    jobId: string,
    groupIndex: number,
    artifactRevision: string,
    size: 'screen' | 'original' = 'screen'
  ): string => {
    const serverUrl = getCachedServerUrl();
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    const params = new URLSearchParams();
    if (size === 'original') params.set('size', size);
    if (artifactRevision) params.set('v', artifactRevision);
    const query = params.size ? `?${params.toString()}` : '';
    return `${basePath}${dbPath(
      dbId,
      `/stack-previews/${encodeURIComponent(jobId)}/${groupIndex}/preview`
    )}${query}`;
  },

  getStackFitsUrl: (
    dbId: string,
    jobId: string,
    groupIndex: number,
    artifactRevision: string
  ): string => {
    const serverUrl = getCachedServerUrl();
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    const revision = artifactRevision ? `?v=${encodeURIComponent(artifactRevision)}` : '';
    return `${basePath}${dbPath(
      dbId,
      `/stack-previews/${encodeURIComponent(jobId)}/${groupIndex}/fits`
    )}${revision}`;
  },

  startMonoArtifactSearch: async (
    dbId: string,
    jobId: string,
    groupIndex: number,
    artifactRevision: string,
    region: ReferenceRegion
  ): Promise<ArtifactSearchJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ArtifactSearchJob>>(
      dbPath(
        dbId,
        `/stack-previews/${encodeURIComponent(jobId)}/${groupIndex}/artifact-searches`
      ),
      { artifact_revision: artifactRevision, region }
    );
    if (!data.data) throw new Error(data.error || 'Failed to start source-frame search');
    return data.data;
  },

  startColorArtifactSearch: async (
    dbId: string,
    jobId: string,
    artifactRevision: string,
    region: ReferenceRegion
  ): Promise<ArtifactSearchJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ArtifactSearchJob>>(
      dbPath(dbId, `/stack-previews/color/${encodeURIComponent(jobId)}/artifact-searches`),
      { artifact_revision: artifactRevision, region }
    );
    if (!data.data) throw new Error(data.error || 'Failed to start source-frame search');
    return data.data;
  },

  getArtifactSearch: async (dbId: string, searchId: string): Promise<ArtifactSearchJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ArtifactSearchJob>>(
      dbPath(dbId, `/stack-previews/artifact-searches/${encodeURIComponent(searchId)}`)
    );
    if (!data.data) throw new Error(data.error || 'Source-frame search not found');
    return data.data;
  },

  getArtifactCropUrl: (dbId: string, searchId: string, imageId: number): string => {
    const serverUrl = getCachedServerUrl();
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    return `${basePath}${dbPath(
      dbId,
      `/stack-previews/artifact-searches/${encodeURIComponent(searchId)}/crops/${imageId}`
    )}`;
  },

  getAppliedStackStretch: async (
    dbId: string,
    jobId: string,
    groupIndex: number,
    revision: string,
    signal?: AbortSignal
  ): Promise<StackStretchPreview | null> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StackStretchPreview | null>>(
      dbPath(dbId, `/stack-previews/${encodeURIComponent(jobId)}/${groupIndex}/stretch`),
      { params: { v: revision }, signal }
    );
    if (data.error) throw new Error(data.error);
    return data.data ? normalizeStretchPreview(data.data) : null;
  },

  clearStackStretch: async (
    dbId: string,
    jobId: string,
    groupIndex: number,
    revision: string
  ): Promise<void> => {
    const apiInstance = await getApi();
    try {
      await apiInstance.delete(
        dbPath(dbId, `/stack-previews/${encodeURIComponent(jobId)}/${groupIndex}/stretch`),
        { params: { v: revision } }
      );
    } catch (cause) {
      throw stackStretchError(cause, 'Failed to revert stack processing');
    }
  },

  applyStackStretch: async (
    dbId: string,
    jobId: string,
    groupIndex: number,
    request: StackViewProcessingRequest,
    options?: {
      /** Live progress from a detached run's 202 polls. */
      onProgress?: (progress: StackStretchPendingProgress) => void;
      signal?: AbortSignal;
      revision?: string;
      /** Poll interval override, for tests. */
      pollIntervalMs?: number;
    }
  ): Promise<StackStretchPreview> => {
    // A long chain (RC-Astro tools take minutes) answers 202 and computes
    // detached; re-sending the identical request polls it — each poll
    // carrying the run's live progress — and finally returns the cached
    // result. The cap is generous: a CPU-only StarXTerminator pass on a
    // large stack legitimately takes a while.
    const deadline = Date.now() + 60 * 60 * 1000;
    let polling = false;
    for (;;) {
      options?.signal?.throwIfAborted();
      try {
        const apiInstance = await getApi();
        const response = await apiInstance.post<
          ApiResponse<StackStretchPreview | StackStretchPendingProgress>
        >(
          dbPath(
            dbId,
            `/stack-previews/${encodeURIComponent(jobId)}/${groupIndex}/stretch`
          ),
          request,
          { signal: options?.signal, params: { v: options?.revision, poll: polling || undefined } }
        );
        if (response.status !== 202) {
          const data = response.data.data as StackStretchPreview | null;
          if (!data) {
            throw new Error(response.data.error || 'Failed to apply stack stretch');
          }
          return normalizeStretchPreview(data);
        }
        const pending = response.data.data as StackStretchPendingProgress | null;
        if (pending && options?.onProgress) options.onProgress(pending);
        polling = true;
      } catch (cause) {
        // A server restarting answers nothing for a while, or a proxy in
        // front of it a 502–504. The run comes back from the server's job
        // journal under the same request, so a poll keeps asking.
        if (!(polling && serverUnreachable(cause))) {
          throw stackStretchError(cause, 'Failed to apply stack stretch');
        }
      }
      if (Date.now() > deadline) {
        throw new Error('Stack processing is still running; try again later');
      }
      await new Promise((resolve) => setTimeout(resolve, options?.pollIntervalMs ?? 5000));
    }
  },

  getStackColorCatalog: async (
    dbId: string,
    projectId: number
  ): Promise<StackColorCatalog> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StackColorCatalog>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/color`)
    );
    if (!data.data) throw new Error(data.error || 'Color preview availability could not be loaded');
    return { ...data.data, jobs: data.data.jobs.map(normalizeColorJob) };
  },

  startStackColor: async (
    dbId: string,
    projectId: number,
    request: {
      target_id: number;
      kind: StackColorKind;
      palette?: StackNarrowbandPalette;
      force?: boolean;
      crop?: StackColorCrop;
      processing?: StackColorProcessing;
      input_sources?: StackColorInputSources;
    }
  ): Promise<StackColorJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<StackColorJob>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/color`),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to start color preview');
    return normalizeColorJob(data.data);
  },

  getStackColorJob: async (
    dbId: string,
    projectId: number,
    jobId: string
  ): Promise<StackColorJob> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StackColorJob>>(
      dbPath(dbId, `/projects/${projectId}/stack-previews/color/${encodeURIComponent(jobId)}`)
    );
    if (!data.data) throw new Error(data.error || 'Color preview job not found');
    return normalizeColorJob(data.data);
  },

  getStackColorPreviewUrl: (
    dbId: string,
    jobId: string,
    artifactRevision: string,
    size: 'screen' | 'original' = 'screen'
  ): string => {
    const serverUrl = getCachedServerUrl();
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    const params = new URLSearchParams();
    if (size === 'original') params.set('size', size);
    if (artifactRevision) params.set('v', artifactRevision);
    const query = params.size ? `?${params.toString()}` : '';
    return `${basePath}${dbPath(
      dbId,
      `/stack-previews/color/${encodeURIComponent(jobId)}/preview`
    )}${query}`;
  },

  getStackColorFitsUrl: (
    dbId: string,
    jobId: string,
    artifactRevision: string
  ): string => {
    const serverUrl = getCachedServerUrl();
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    const revision = artifactRevision ? `?v=${encodeURIComponent(artifactRevision)}` : '';
    return `${basePath}${dbPath(
      dbId,
      `/stack-previews/color/${encodeURIComponent(jobId)}/fits`
    )}${revision}`;
  },

  getImages: async (dbId: string, query: ImageQuery): Promise<Image[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<Image[]>>(dbPath(dbId, '/images'), {
      params: query,
    });
    return data.data || [];
  },

  getImage: async (dbId: string, imageId: number): Promise<Image> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<Image>>(
      dbPath(dbId, `/images/${imageId}`)
    );
    if (!data.data) throw new Error('Image not found');
    return data.data;
  },

  getImageAstrometry: async (dbId: string, imageId: number): Promise<AstrometryAnalysis> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<AstrometryAnalysis>>(
      dbPath(dbId, `/images/${imageId}/astrometry`)
    );
    if (!data.data) throw new Error(data.error || 'Astrometry analysis unavailable');
    return data.data;
  },

  solveImageAstrometry: async (dbId: string, imageId: number): Promise<AstrometryAnalysis> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<AstrometryAnalysis>>(
      dbPath(dbId, `/images/${imageId}/astrometry`)
    );
    if (!data.data) throw new Error(data.error || 'Plate solve failed');
    return data.data;
  },

  getImageSatellites: async (dbId: string, imageId: number): Promise<SatelliteAnalysisStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<SatelliteAnalysisStatus>>(
      dbPath(dbId, `/images/${imageId}/satellites`)
    );
    if (!data.data) throw new Error(data.error || 'Satellite analysis unavailable');
    return data.data;
  },

  predictImageSatellites: async (dbId: string, imageId: number): Promise<SatelliteAnalysis> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<SatelliteAnalysis>>(
      dbPath(dbId, `/images/${imageId}/satellites`)
    );
    if (!data.data) throw new Error(data.error || 'Satellite prediction failed');
    return data.data;
  },

  updateImageGrade: async (
    dbId: string,
    imageId: number,
    request: UpdateGradeRequest
  ): Promise<void> => {
    const apiInstance = await getApi();
    await apiInstance.put(dbPath(dbId, `/images/${imageId}/grade`), request);
  },

  /** Grade many images in one request and one server transaction. Returns
   * the grades it replaced so callers can record undo state without a
   * request per image. */
  batchUpdateImageGrades: async (
    dbId: string,
    updates: BatchGradeEntry[]
  ): Promise<BatchGradeResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<BatchGradeResponse>>(
      dbPath(dbId, '/images/grade'),
      { updates }
    );
    return data.data!;
  },

  getStarDetection: async (
    dbId: string,
    imageId: number,
    copy?: string
  ): Promise<StarDetectionResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<StarDetectionResponse>>(
      dbPath(dbId, `/images/${imageId}/stars`),
      copy ? { params: { copy } } : undefined
    );
    if (!data.data) throw new Error('Star detection failed');
    return data.data;
  },

  // Batch readiness poll for on-demand previews/annotated images. One request
  // for a whole grid of pending images instead of one poll per image. Returns
  // statuses parallel to `requests`.
  getGenerationStatus: async (
    dbId: string,
    requests: PreviewDescriptor[]
  ): Promise<GenerationStatus[]> => {
    const apiInstance = await getApi();
    // Three endpoints answer: light previews, a stack's masters, and the
    // calibration library's frames and masters.
    const route = (d: PreviewDescriptor) =>
      d.kind === 'calibration_frame' || d.kind === 'flat_ratio'
        || (d.kind === 'calibration_master' && d.source.kind === 'library')
        ? 'library'
        : d.kind === 'calibration_master' ? 'stack' : 'image';
    const imageRequests = requests.filter((d) => route(d) === 'image') as ImagePreviewDescriptor[];
    const masterRequests = requests.filter((d) => route(d) === 'stack') as (
      CalibrationMasterPreviewDescriptor & { source: StackMasterSource }
    )[];
    const libraryRequests = requests.filter((d) => route(d) === 'library') as (
      CalibrationFramePreviewDescriptor | CalibrationMasterPreviewDescriptor | FlatRatioPreviewDescriptor
    )[];
    const imageRequest = imageRequests.length ? apiInstance.post<
      ApiResponse<{ statuses: GenerationStatus[] }>
    >(dbPath(dbId, '/images/generation-status'), {
      requests: imageRequests.map((d) => ({
        image_id: d.imageId,
        kind: d.kind,
        size: d.size,
        stretch: d.stretch,
        midtone: d.midtone,
        shadow: d.shadow,
        max_stars: d.maxStars,
        color: d.color,
        copy: d.copy,
      })),
    }).then(({ data }) => data.data?.statuses ?? []) : Promise.resolve([]);
    const masterRequest = masterRequests.length ? apiInstance.post<
      ApiResponse<{ statuses: GenerationStatus[] }>
    >(dbPath(dbId, '/stack-previews/calibration-masters/generation-status'), {
      requests: masterRequests.map((d) => ({
        source: {
          kind: d.source.kind,
          job_id: d.source.jobId,
          ...(d.source.kind === 'mono' ? { group_index: d.source.groupIndex } : {}),
          artifact_revision: d.source.artifactRevision,
        },
        master_id: d.masterId,
        size: d.size,
        midtone: d.midtone,
        shadow: d.shadow,
      })),
    }).then(({ data }) => data.data?.statuses ?? []) : Promise.resolve([]);
    const libraryRequest = libraryRequests.length ? apiInstance.post<
      ApiResponse<{ statuses: GenerationStatus[] }>
    >(dbPath(dbId, '/calibrations/generation-status'), {
      requests: libraryRequests.map((d) => d.kind === 'calibration_frame'
        ? { target: 'frame', id: d.frameUuid, view: d.view, size: d.size, stretch: d.stretch }
        : d.kind === 'flat_ratio'
          ? { target: 'ratio', earlier: d.earlier, later: d.later, stretch: d.stretch }
          : { target: 'master', id: d.masterId, size: d.size, midtone: d.midtone, shadow: d.shadow }),
    }).then(({ data }) => data.data?.statuses ?? []) : Promise.resolve([]);
    // An unavailable endpoint must not stall unrelated previews in this batch.
    const [images, masters, library] = await Promise.allSettled([imageRequest, masterRequest, libraryRequest]);
    const statuses = {
      image: images.status === 'fulfilled' ? images.value : [],
      stack: masters.status === 'fulfilled' ? masters.value : [],
      library: library.status === 'fulfilled' ? library.value : [],
    };
    const next = { image: 0, stack: 0, library: 0 };
    return requests.map((d) => {
      const kind = route(d);
      return statuses[kind][next[kind]++] ?? { state: 'generating' };
    });
  },

  getStackCalibrationMasters: async (
    dbId: string,
    source: StackMasterSource
  ): Promise<StackCalibrationMasters> => {
    const apiInstance = await getApi();
    const artifactPath = source.kind === 'mono'
      ? `${encodeURIComponent(source.jobId)}/${source.groupIndex}`
      : `color/${encodeURIComponent(source.jobId)}`;
    const { data } = await apiInstance.get<ApiResponse<StackCalibrationMasters>>(
      dbPath(dbId, `/stack-previews/${artifactPath}/calibration-masters`),
      { params: { revision: source.artifactRevision } }
    );
    if (!data.data) throw new Error(data.error || 'Failed to load calibration masters');
    return {
      ...data.data,
      masters: data.data.masters.map((master) => ({
        ...master,
        preview_url: master.preview_url ? withServerUrl(master.preview_url) : null,
        original_preview_url: master.original_preview_url ? withServerUrl(master.original_preview_url) : null,
        fits_url: master.fits_url ? withServerUrl(master.fits_url) : null,
      })),
    };
  },

  getPreviewUrl: (dbId: string, imageId: number, options?: PreviewOptions): string => {
    const serverUrl = getCachedServerUrl();
    const params = new URLSearchParams();
    params.set('rv', RENDERED_ARTIFACT_URL_VERSION);
    if (options?.size) params.append('size', options.size);
    if (options?.stretch !== undefined) params.append('stretch', String(options.stretch));
    if (options?.midtone !== undefined) params.append('midtone', String(options.midtone));
    if (options?.shadow !== undefined) params.append('shadow', String(options.shadow));
    // Sent explicitly either way, so the URL states what it wants rather than
    // depending on the server's default staying put.
    if (options?.color !== undefined) params.append('color', String(options.color));
    if (options?.copy) params.append('copy', options.copy);

    const queryString = params.toString();
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    return `${basePath}${dbPath(dbId, `/images/${imageId}/preview`)}${
      queryString ? `?${queryString}` : ''
    }`;
  },

  getAnnotatedUrl: (
    dbId: string,
    imageId: number,
    size: 'screen' | 'large' | 'original' = 'large',
    maxStars?: number,
    copy?: string
  ): string => {
    const serverUrl = getCachedServerUrl();
    const params = new URLSearchParams();
    params.set('rv', RENDERED_ARTIFACT_URL_VERSION);
    params.append('size', size);
    if (maxStars !== undefined) {
      params.append('max_stars', String(maxStars));
    }
    if (copy) params.append('copy', copy);
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    return `${basePath}${dbPath(dbId, `/images/${imageId}/annotated`)}?${params.toString()}`;
  },

  getPsfUrl: (
    dbId: string,
    imageId: number,
    options?: {
      num_stars?: number;
      psf_type?: string;
      sort_by?: string;
      grid_cols?: number;
      selection?: string;
      copy?: string;
    }
  ): string => {
    const serverUrl = getCachedServerUrl();
    const params = new URLSearchParams();
    if (options?.copy) params.append('copy', options.copy);
    if (options?.num_stars) params.append('num_stars', String(options.num_stars));
    if (options?.psf_type) params.append('psf_type', options.psf_type);
    if (options?.sort_by) params.append('sort_by', options.sort_by);
    if (options?.grid_cols) params.append('grid_cols', String(options.grid_cols));
    if (options?.selection) params.append('selection', options.selection);

    const queryString = params.toString();
    const basePath = serverUrl ? `${serverUrl}/api` : '/api';
    return `${basePath}${dbPath(dbId, `/images/${imageId}/psf`)}${
      queryString ? `?${queryString}` : ''
    }`;
  },

  getProjectsOverview: async (dbId: string): Promise<ProjectOverview[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ProjectOverview[]>>(
      dbPath(dbId, '/projects/overview')
    );
    return data.data || [];
  },

  getProjectScheduler: async (
    dbId: string,
    projectId: number
  ): Promise<ProjectSchedulerDetails> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ProjectSchedulerDetails>>(
      dbPath(dbId, `/projects/${projectId}/scheduler`)
    );
    if (!data.data) throw new Error(data.error || 'Failed to load project schedule');
    return data.data;
  },

  createExposurePlan: async (
    dbId: string,
    targetId: number,
    request: CreateExposurePlanRequest
  ): Promise<ExposurePlanDetails> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<ExposurePlanDetails>>(
      dbPath(dbId, `/targets/${targetId}/exposure-plans`),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to create exposure plan');
    return data.data;
  },

  updateExposurePlan: async (
    dbId: string,
    planId: number,
    request: { exposure: number; desired: number; enabled: boolean }
  ): Promise<void> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<{ updated: boolean }>>(
      dbPath(dbId, `/exposure-plans/${planId}`),
      request
    );
    if (!data.data) throw new Error(data.error || 'Failed to update exposure plan');
  },

  getTargetsOverview: async (dbId: string): Promise<TargetOverview[]> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<TargetOverview[]>>(
      dbPath(dbId, '/targets/overview')
    );
    return data.data || [];
  },

  getSkyCoverage: async (dbId: string): Promise<SkyCoverage> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<SkyCoverage>>(
      dbPath(dbId, '/sky/coverage')
    );
    if (!data.data) throw new Error('Failed to get sky coverage');
    return {
      ...data.data,
      targets: data.data.targets.map((target) => ({
        ...target,
        preview: target.preview
          ? { ...target.preview, url: withServerUrl(target.preview.url) }
          : target.preview,
      })),
    };
  },

  getOverallStats: async (dbId: string): Promise<OverallStats> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<OverallStats>>(
      dbPath(dbId, '/stats/overall')
    );
    if (!data.data) throw new Error('Failed to get overall stats');
    return data.data;
  },

  getCacheProgress: async (dbId: string): Promise<CacheRefreshProgress> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CacheRefreshProgress>>(
      dbPath(dbId, '/cache-progress')
    );
    if (!data.data) throw new Error('Failed to get cache progress');
    return data.data;
  },

  analyzeSequence: async (
    dbId: string,
    request: SequenceAnalysisRequest | ProjectSequenceAnalysisRequest | DatabaseSequenceAnalysisRequest
  ): Promise<SequenceAnalysisResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<SequenceAnalysisResponse>>(
      dbPath(dbId, '/analysis/sequence'),
      { params: request }
    );
    if (!data.data) throw new Error('Sequence analysis failed');
    return data.data;
  },

  getImageQuality: async (
    dbId: string,
    imageId: number,
    penalties?: PenaltyScaleParams
  ): Promise<ImageQualityResponse> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<ImageQualityResponse>>(
      dbPath(dbId, `/analysis/image/${imageId}`),
      { params: penalties }
    );
    if (!data.data) throw new Error('Image quality data not found');
    return data.data;
  },

  startSpatialScan: async (
    dbId: string,
    request: SpatialScanRequest
  ): Promise<SpatialScanStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<SpatialScanStatus>>(
      dbPath(dbId, '/analysis/quality-scan'),
      request
    );
    if (!data.data) throw new Error('Failed to start spatial scan');
    return data.data;
  },

  getSpatialScanStatus: async (
    dbId: string,
    request?: SpatialScanStatusRequest
  ): Promise<SpatialScanStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<SpatialScanStatus>>(
      dbPath(dbId, '/analysis/quality-scan'),
      { params: request }
    );
    if (!data.data) throw new Error('Failed to get spatial scan status');
    return data.data;
  },

  startQualityBackfill: async (
    dbId: string,
    request: QualityBackfillRequest
  ): Promise<QualityBackfillStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<QualityBackfillStatus>>(
      dbPath(dbId, '/analysis/quality-backfill'),
      request
    );
    if (!data.data) throw new Error('Failed to start database quality analysis');
    return data.data;
  },

  /** Where the catalog's reject removal job stands, with the latest
   *  preview and outcome. */
  getRejectRemoval: async (dbId: string): Promise<import('./types').RejectRemovalStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<import('./types').RejectRemovalStatus>>(
      dbPath(dbId, '/rejects/removal'));
    if (!data.data) throw new Error(data.error || 'Failed to read reject removal');
    return data.data;
  },

  /** Start working out what removing old rejects would take; changes
   *  nothing. Answers with the job's status. */
  previewRejectRemoval: async (dbId: string, minAgeDays: number): Promise<import('./types').RejectRemovalStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').RejectRemovalStatus>>(
      dbPath(dbId, '/rejects/removal/preview'), { min_age_days: minAgeDays });
    if (!data.data) throw new Error(data.error || 'Failed to preview removing rejects');
    return data.data;
  },

  /** Start removing what the preview showed. A removal that finds the
   *  rejects changed removes nothing and leaves the new preview. */
  applyRejectRemoval: async (dbId: string, minAgeDays: number, digest: string, retentionDays: number): Promise<import('./types').RejectRemovalStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').RejectRemovalStatus>>(
      dbPath(dbId, '/rejects/removal/apply'), { min_age_days: minAgeDays, digest, retention_days: retentionDays });
    if (!data.data) throw new Error(data.error || 'Failed to remove rejects');
    return data.data;
  },

  /** Stop the reject removal job between frames. */
  stopRejectRemoval: async (dbId: string): Promise<import('./types').RejectRemovalStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').RejectRemovalStatus>>(
      dbPath(dbId, '/rejects/removal/stop'));
    if (!data.data) throw new Error(data.error || 'Failed to stop reject removal');
    return data.data;
  },

  getRemovedRejects: async (dbId: string): Promise<import('./types').RemovedRejects> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<import('./types').RemovedRejects>>(dbPath(dbId, '/rejects/removed'));
    if (!data.data) throw new Error(data.error || 'Failed to list removed rejects');
    return data.data;
  },

  /** Start bringing a removal batch back. Answers with the job's status. */
  restoreRemovedRejects: async (dbId: string, batchId: string): Promise<import('./types').RejectRemovalStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').RejectRemovalStatus>>(
      dbPath(dbId, '/rejects/removed/restore'), { batch_id: batchId });
    if (!data.data) throw new Error(data.error || 'Failed to restore removed rejects');
    return data.data;
  },

  /** Forget the saved rows of one emptied removal batch; markers stay. */
  purgeRemovedRejects: async (dbId: string, batchId: string): Promise<import('./types').RejectPurgeReport> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').RejectPurgeReport>>(
      dbPath(dbId, '/rejects/removed/purge'), { batch_id: batchId });
    if (!data.data) throw new Error(data.error || 'Failed to purge removed rejects');
    return data.data;
  },

  /** Start deleting the files of removals past their retention. Answers
   *  with the job's status. */
  emptyRejectTrash: async (dbId: string): Promise<import('./types').RejectRemovalStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.post<ApiResponse<import('./types').RejectRemovalStatus>>(dbPath(dbId, '/rejects/trash/empty'));
    if (!data.data) throw new Error(data.error || 'Failed to empty the trash');
    return data.data;
  },

  /** Whether this database pairs calibrated copies, and how many it has. */
  getCalibratedCopies: async (dbId: string): Promise<CalibratedCopies> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<CalibratedCopies>>(
      dbPath(dbId, '/calibrated-copies')
    );
    if (!data.data) throw new Error(data.error || 'Failed to read the calibrated copies');
    return data.data;
  },

  /** Change either setting; a field left out keeps its value. */
  updateCalibratedCopies: async (
    dbId: string,
    settings: { pair?: boolean; scan_calibrated?: boolean }
  ): Promise<CalibratedCopies> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<CalibratedCopies>>(
      dbPath(dbId, '/calibrated-copies'),
      settings
    );
    if (!data.data) throw new Error(data.error || 'Failed to save the setting');
    return data.data;
  },

  /** Analyze frames as they arrive by sync, upload or auto-import. */
  setAnalyzeNewFrames: async (dbId: string, enabled: boolean): Promise<QualityBackfillStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.put<ApiResponse<QualityBackfillStatus>>(
      dbPath(dbId, '/analysis/quality-backfill/new-frames'),
      { enabled }
    );
    if (!data.data) throw new Error(data.error || 'Failed to save the setting');
    return data.data;
  },

  getQualityBackfillStatus: async (dbId: string): Promise<QualityBackfillStatus> => {
    const apiInstance = await getApi();
    const { data } = await apiInstance.get<ApiResponse<QualityBackfillStatus>>(
      dbPath(dbId, '/analysis/quality-backfill')
    );
    if (!data.data) throw new Error('Failed to get database quality status');
    return data.data;
  },
};
